import { recordUsage, noticeTemplate } from '../usage';
import { editorViewMenu } from './editorViewMenu';
import { inkToolbar } from './inkToolbar';
import { SvgEditorHost } from './SvgEditor';
import { ToolbarDock } from './tbdock';
import { ContextBars, contextBars } from './ctxdock';
import { StatsDialog } from './StatsDialog';
import { GoogleDocsDialog } from './GoogleDocs';
import { LessWrongDialog } from './LessWrong';
import { documentMenus } from './documentMenus';
import { referenceTransaction } from '../editor/references';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'preact/hooks';
import type { EditorView } from 'prosemirror-view';
import { NodeSelection, TextSelection } from 'prosemirror-state';
import { api, googleSignInUrl, knownProjects, type AiStatus, type BibAddResult, type DocMeta, type Project, type User, fileUrl, fileTabId } from '../api';
import { getPrefs, setPref, subscribePrefs, type Prefs } from '../prefs';
import { openRewrite, REWRITE_KEY } from '../editor/ai/rewrite';
import { Login } from './Login';
import { DocPanel } from './DocPanel';
import { DRAWER_LAYOUT_QUERY, foldDrawersOnNarrow } from './drawerLayout';
import { Home, projectDocs, ProjectRootPanel } from './Home';
import { pendingImportFlag } from './pendingImport';
import { TextEditor } from './TextEditor';
import { isMarkdownDoc } from '../editor/markdown';
import { isArticleDoc, htmlKindKnown, htmlKindsUnsure, onArticleKinds } from '../articlekinds';
import { applyArticleLook } from '../editor/article';
import { ArticlePage } from './ArticlePage';
import { PaneSwitch } from './PaneSwitch';
import { PaneDividers } from './PaneDividers';
import { EditModeSwitch, editModeOf, applyEditMode, modeScope, setOwnSuggesting, suggestingFor, switchModeScope, type ModeScope } from './EditModeSwitch';
import { loadLayout, saveLayout, setPaneShown, togglePane, visiblePanes, paneGrow, applyPreset, presetMatches, parseViewParam, viewParam, type LinkView, type PaneId, type PaneLayout, type ViewKind } from './panes';
import { ShareDialog } from './Share';
import { CopyLinkDialog } from './CopyLinkDialog';
import { GuestCallout, ShareInvite, type SharePreview } from './Guest';
import { GitDialog } from './Git';
import { MenuBar, openPalette, PALETTE_LABEL, PALETTE_DEFAULT, type MenuDef } from './MenuBar';
import { setThemePref, useTheme } from './theme';
import { useUiMode, setUiMode, adoptAccountUi, uiChoiceVersion } from './uimode';
import { usePresentation } from './presentation';
import { Toolbar, NAMED_COLORS, type ToolButton } from './Toolbar';
import { buildToolbars, loadToolbarPrefs, mathExecutor, useMathPanels, toolbarClipboard, markValue, MODERN_COLLAPSE, type ToolbarId, type ToolbarMode, type ToolbarPrefs } from './toolbars';
import { SelectionBar } from './SelectionBar';
import { setDefaultZoom } from './zoomkeys';
import { debounce, hashAuthor, applyAuthorColors, applyCommentAvatars, bcp47, suggestLabel, LayoutPicker, documentStats, applyEditorZoom, stepTextZoom, useDocumentViewportZoom, SidebarGrip, restoreSidebarWidths } from './shellutil';
import { chooseLyxImport } from './lyximport';
import { setDocumentFonts } from '../fonts/editorfont';
import { setDocumentParagraphs, setDocumentHeadings } from '../editor/paragraphsep';
import { TextStylesPanel } from './TextStyles';
import { setDocumentNumbering } from '../editor/plugins/numbering';
import { MarkdownSettingsDialog } from './MarkdownSettings';
import { Outline, buildOutline, type OutlineItem } from './Outline';
import { Comments } from './Comments';
import { Versions } from './Versions';
import { AgentPanel, askAgentAbout } from './AgentPanel';
import { inlineModelLabel } from './AgentModelPicker';
import { PdfPanel, stateFromBuild, jobActive, EMPTY_PDF, type PdfState } from './PdfPanel';
import { Ruler, NOTE_SCALE_DEFAULT, NOTE_SCALE_MIN, NOTE_SCALE_MAX, MIN_WIDTH, MAX_WIDTH } from './Ruler';
import { useNotesPane, useNotesFold, NotesSash, NotesUnfoldTab, NotesResolved } from './notespane';
import { StatusBar, SaveIndicator, ZoomControl, type Status } from './StatusBar';
import { CornerButton } from './CornerButton';
import { SyncChip, useExternalSync } from './SyncChip';
import { SourcePane, type SourceTarget, cursorLine, docBlocks, blockPos } from './SourcePane';
import { activeMathField, mathFocusListeners, mathCursorListeners, type LyxMathField } from '../editor/lyxmath/field';
import { Tour, InterfacePrompt, tourWanted, rememberTour, type TourEnd } from './Tour';
import { FeedbackDialog } from './Feedback';
import { Dialog, GraphicsDialog, TableDialog, LabelDialog, RefDialog, CiteDialog, HrefDialog, SettingsDialog, InsetDialog, HelpDialog, TexDialog, MacrosDialog, ParagraphDialog, TableSettingsDialog, DelimiterDialog, MatrixDialog, commandParams, HELP_ROWS, AiRepairDialog, ChildDocDialog, DialogHost, uiPrompt, uiConfirm, uiAlert } from './Dialogs';
import { promptNewFile } from './newfile';
import { SettingsPanel } from './Settings';
import { clearLocalData } from './localData';
import { createEditor, moveLocalCopies, type EditorHandle, type SaveState } from '../editor/editor';
import { refreshMacros } from '../editor/macrodefs';
import { describeChange } from '../editor/assembly';
import { useProjectEvents, deletedHere, siblingDocument } from './FileBrowser';
import { newerVersionAvailable, reloadHeld } from './update';
import { generateLyx } from './SourcePane';
import { editorContext, viewDocId } from '../editor/context';
import { parsePlace, showPlace, webAppLink, type Place } from '../editor/permalink';
import { navHistory, type NavLocation } from './navhistory';
import { restoredCursorPos } from '../editor/cursormemory';
import { PdfViewer, type PdfTarget } from './PdfViewer';
import { locateSourceLine } from './sourcelocate';
import { canonical, effectiveShortcut, keyFromEvent, syncBindings } from './keybindings';
import { STANDARD_LAYOUTS, sectionLevel } from '../editor/layouts';
import { chordKey } from '../editor/keymap';
import * as C from '../editor/commands';
import { setMarginMode } from '../editor/plugins/margin';
import { setPagesView, setPageBreaks, pageAtCursor, setPagesHost } from '../editor/plugins/pages';
import { setInk, getInk, subscribeInk, isTabletClient } from '../editor/plugins/ink';
import { BoardEditor } from './BoardEditor';
import { DeliverableEditor } from '../deliverable/DeliverableEditor';
import { useEditorMenus } from './editormenus';
import { noteShown } from './lastfile';
import { NewLayoutDialog } from './NewLayoutDialog';
import { setLayoutHeader, layoutZoomStep, refreshLayoutCheck } from '../editor/layout/controller';
import { acceptAllChanges, rejectAllChanges, changeAt, hasChanges, changesFilterKey } from '../editor/plugins/changes';
import * as T from '../editor/tablecommands';
import type { PresenceUser } from '../editor/editor';
import { setQuery, findNext, replaceCurrent, replaceAll, findKey } from '../editor/plugins/find';
import { schema, unquote, projectOfDoc, docPathOf, docDirOf, projectShortName, spacingStretch, spacingValue, splitDocId, isProjectKey } from '@overlyx/core';

type Dialog = { name: string; arg?: unknown } | null;

/** LyX language name → BCP 47 tag (for the browser's spell checker). */
export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [google, setGoogle] = useState(false);
  const [ready, setReady] = useState(false);
  // why the share link in the URL did not open (shown on the sign-in page)
  const [linkNote, setLinkNote] = useState<string | null>(null);
  // a share link opened without a session: what it opens, for the page that asks how to come in
  const [invite, setInvite] = useState<{ token: string; path: string | null; info: SharePreview } | null>(null);
  useEffect(() => {
    api.me().then(async r => {
      let u = r.user;
      // a share link opened without an account: first the choice — sign in (Workspace then joins
      // the project through the link still in the URL) or come in as a guest (joinAsGuest)
      const token = u ? null : parseHash().share;
      if (token) {
        try { setInvite({ token, path: parseHash().sharePath, info: await api.sharePreview(token) }); }
        catch (e) { setLinkNote((e as Error).message); }
      } else if (!u) {
        // a public project's address: the visitor reads it as a guest, no sign-in
        const v = await visitPublic();
        if (v?.user) u = v.user;
      }
      setUser(u); setGoogle(r.google);
      if (u) void syncBindings();   // account shortcuts follow the user across browsers
      // remembered for offline starts (the session cookie itself is still valid then)
      try { if (u) localStorage.setItem('ol.user', JSON.stringify(u)); else localStorage.removeItem('ol.user'); } catch { /* ignore */ }
    }).catch(() => {
      // no server (offline): continue with the last known user; documents come from the local copies
      try { const u = localStorage.getItem('ol.user'); if (u) setUser(JSON.parse(u)); } catch { /* ignore */ }
    }).finally(() => setReady(true));
  }, []);
  // a guest asked to sign in (no Google here, or the callout's plain button): the sign-in page over
  // the workspace — the guest cookie stays, so the login moves the guest's projects to the account
  const [wantLogin, setWantLogin] = useState(false);
  // an old link in the URL is replaced by the document's current id before the workspace opens it
  // (later changes of the hash: Workspace's hashchange handler)
  const [linkChecked, setLinkChecked] = useState<number | null>(null);
  useEffect(() => {
    if (!user) return;
    let live = true;
    void rekeyLocalCopies().then(canonicalHash).then(() => visitPublic()).finally(() => { if (live) setLinkChecked(user.id); });
    return () => { live = false; };
  }, [user?.id]);
  const acceptLogin = (u: User) => {
    // A native sign-in does not rerun the initial identity effect. Remember this
    // authenticated user too, and replace the worker's pre-login anonymous cache.
    try { localStorage.setItem('ol.user', JSON.stringify(u)); } catch { /* private mode */ }
    setUser(u);
    void api.me().catch(() => {});
  };
  // "Continue without an account" on the invitation: the server opens a guest session holding what
  // the link grants, straight to the document — signing in later keeps the project (Workspace's guest callout)
  const joinAsGuest = async (name: string) => {
    if (!invite) return;
    const a = await api.acceptShare(invite.token, invite.path, name);
    const u = a.user ?? (await api.me()).user;
    if (!u) throw new Error('The project could not be opened. Try again, or sign in.');
    location.hash = shareLanding(a);
    setInvite(null);
    acceptLogin(u);
    void syncBindings();
  };
  if (!ready || (user && linkChecked !== user.id)) return <div style="padding:40px;color:#666">Loading…</div>;
  if (!user && invite) {
    return wantLogin
      ? <Login google={google} onLogin={u => { setInvite(null); setWantLogin(false); acceptLogin(u); }} onBack={() => setWantLogin(false)} backLabel="← Back to the invitation" note={`Sign in to join “${invite.info.title ?? projectShortName(invite.info.project)}”.`} />
      : <ShareInvite invite={invite.info} google={google} onGuest={joinAsGuest} onSignIn={() => setWantLogin(true)} />;
  }
  if (!user) return <Login google={google} onLogin={acceptLogin} note={linkNote} />;
  if (user.guest && wantLogin) return <Login google={google} onLogin={u => { acceptLogin(u); setWantLogin(false); }} onBack={() => setWantLogin(false)} note="Sign in to keep the shared project in your account." />;
  return <Workspace user={user} google={google} onSignIn={() => setWantLogin(true)} onLogout={() => api.logout().then(clearLocalData).then(() => { try { localStorage.removeItem('ol.user'); } catch { /* ignore */ } setUser(null); })} />;
}

type RightTab = 'comments' | 'versions' | 'agent';
const RIGHT_TABS = ['comments', 'versions', 'agent'] as const;
const RIGHT_TAB_LABELS: Record<RightTab, string> = { comments: 'Comments', versions: 'Versions', agent: 'Agent' };
const RIGHT_TAB_TITLES: Record<RightTab, string> = { comments: 'Comment threads: open ones and the resolved archive', versions: 'Versions of this document', agent: 'Agents: the coding agent (OpenAI Codex) working in this project, and your agents connected from elsewhere (Claude Code, Codex …)' };
const PDF_TITLE = 'The PDF beside the text (Ctrl+R builds it)';
/** below this width one pane at a time (the pane switch works like tabs) */
const NARROW_PANES = '(max-width: 760px)';
/** the narrowest the document pane gets beside the PDF before the side panels fold away (px) */
const READABLE_DOC = 600;
function useMedia(query: string): boolean {
  const [on, setOn] = useState(() => typeof matchMedia === 'function' && matchMedia(query).matches);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const m = matchMedia(query);
    const l = () => setOn(m.matches);
    m.addEventListener('change', l);
    return () => m.removeEventListener('change', l);
  }, [query]);
  return on;
}
const LEFT_TITLE = 'The project\'s files; documents expand into their outlines (Ctrl+Alt+O)';
const SOURCE_TITLE = 'LaTeX source beside the text (Ctrl+Alt+S)';
const stored = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };

/**
 * `#/project/path.tex?goto=label` or `?heading=<n>` (the n-th heading), a place a link names
 * (`?text=…`, `?line=…`, `?page=…`, `?slide=…`: editor/permalink.ts) and the view it opens in
 * (`?view=wysiwym,pdf`: app/panes.ts — the id then has `raw:` exactly when it shows the source), or a share link
 * `#/<owner>/<project>/<path>?share=<token>` — the file it lands on, the token joins the project
 * (from before: `#/share/<token>` and `#/share/<token>/<path>`)
 */
function parseHash(): { id: string | null; goto: string | null; heading: number | null; place: Place | null; view: LinkView | null; share: string | null; sharePath: string | null } {
  const raw = location.hash.replace(/^#\/?/, '');
  const q = raw.indexOf('?');
  const idPart = decodeURIComponent(q >= 0 ? raw.slice(0, q) : raw);
  if (idPart.startsWith('share/')) {
    const rest = idPart.slice('share/'.length);
    const cut = rest.indexOf('/');
    return { id: null, goto: null, heading: null, place: null, view: null, share: cut < 0 ? rest : rest.slice(0, cut), sharePath: cut < 0 ? null : rest.slice(cut + 1) || null };
  }
  const params = q >= 0 ? new URLSearchParams(raw.slice(q + 1)) : null;
  const token = params?.get('share');
  if (token) return { id: null, goto: null, heading: null, place: null, view: null, share: token, sharePath: docPathOf(idPart.replace(/^(raw|text|pdf):/, '')) || null };
  const h = params?.get('heading');
  // markdown files opened in the plain text editor before they were documents: links stay valid
  let id = idPart.startsWith('text:') && isMarkdownDoc(idPart) ? idPart.slice(5) : idPart;
  const view = id && !/^(text|pdf):/.test(id) ? parseViewParam(params?.get('view')) : null;
  if (view) id = (view.panes.includes('tex') ? 'raw:' : '') + id.replace(/^raw:/, '');
  return { id: id || null, goto: params?.get('goto') ?? null, heading: h !== null && h !== undefined && /^\d+$/.test(h) ? Number(h) : null, place: parsePlace(params), view, share: null, sharePath: null };
}

/** What kind of document a link's `view=` speaks of (its words for the source and the output: app/panes.ts). */
const viewKindOf = (id: string): ViewKind => (isArticleDoc(id) ? 'article' : isMarkdownDoc(id) ? 'markdown' : 'tex');

/**
 * Where a share link lands (the hash): the linked file when it opens in a tab, else the project's
 * main document — with what else the link's query names, its place and view (editor/permalink.ts, panes.ts).
 */
function shareLanding(r: { project: string; doc: string | null; file: Parameters<typeof fileTabId>[1] | null }): string {
  const id = (r.file && fileTabId(r.project, r.file)) || r.doc;
  if (!id) return '';
  const q = new URLSearchParams(location.hash.split('?')[1] ?? '');
  q.delete('share');
  const rest = q.toString().replace(/\+/g, '%20');
  return '#/' + id + (rest ? '?' + rest : '');
}

/**
 * Open the public project the address names (`#/<owner>/<project>[/<path>]`) — someone else's that
 * this person has no other way into: the server puts it on their project list, and lets a visitor
 * without a session in as a guest (`user`). An address naming only the project goes on to its main
 * document. Null: not a public project, or the server is out of reach.
 */
async function visitPublic(): Promise<Awaited<ReturnType<typeof api.visitPublic>> | null> {
  const h = parseHash();
  if (h.share || !h.id) return null;
  const { project, path } = splitDocId(h.id.replace(/^(raw|text|pdf):/, ''));
  if (!isProjectKey(project) || knownProjects.has(project)) return null;
  try {
    const v = await Promise.race([api.visitPublic(project, path || null), new Promise<null>(res => setTimeout(res, 3000, null))]);
    if (v?.public && v.role === 'view' && !path) location.replace(shareLanding(v) || location.hash);
    return v;
  } catch { return null; }
}

/**
 * A link that names a document by an old id — from before projects lived in their owner's
 * namespace (`#/thesis/main.tex`, now `#/jan/thesis/main.tex`) or before a project changed owner:
 * the server names its current id, which replaces the old one in the URL (history entry included).
 * Ids of projects the server listed are current already; offline the URL stays as it is.
 */
async function canonicalHash(): Promise<boolean> {
  const raw = location.hash.replace(/^#\/?/, '');
  const q = raw.indexOf('?');
  let idPart: string;
  try { idPart = decodeURIComponent(q >= 0 ? raw.slice(0, q) : raw); } catch { return false; }
  if (!idPart || idPart.startsWith('share/') || parseHash().share) return false;
  const prefix = /^(raw|text|pdf):/.exec(idPart)?.[0] ?? '';
  const id = idPart.slice(prefix.length);
  if (knownProjects.has(projectOfDoc(id))) return false;
  try {
    const r = await Promise.race([api.resolveId(id), new Promise<null>(res => setTimeout(res, 3000, null))]);
    if (!r || r.id === id || location.hash.replace(/^#\/?/, '') !== raw) return false;
    location.replace('#/' + prefix + r.id + (q >= 0 ? raw.slice(q) : ''));
    return true;
  } catch { return false; /* offline, or no such document: as it is */ }
}

/**
 * Once per browser: the local copies of documents (editor.ts) named by ids from before owner
 * namespaces move to the documents' current ids, so offline edits not yet sent are not stranded.
 * Offline it is tried again at the next start.
 */
async function rekeyLocalCopies(): Promise<void> {
  const FLAG = 'ol.localCopiesKeyed';
  try { if (localStorage.getItem(FLAG)) return; } catch { return; }
  try {
    const dbs = await (indexedDB as any).databases?.() as { name?: string }[] | undefined;
    const ids = (dbs ?? []).map(d => d.name ?? '').filter(n => n.startsWith('overlyx:')).map(n => n.slice('overlyx:'.length));
    if (ids.length) await Promise.race([api.resolveIds(ids).then(r => moveLocalCopies(r.ids)), new Promise((_, no) => setTimeout(no, 8000, new Error('timeout')))]);
    localStorage.setItem(FLAG, '1');
  } catch { /* offline: next time */ }
}

/** Navigate ▸ Back / Forward (navhistory.ts); the ids are the menu paths, so the palette can rebind them */
const NAV_BACK_ID = 'Navigate ▸ Back', NAV_FORWARD_ID = 'Navigate ▸ Forward';
const NAV_BACK_KEY = 'Ctrl+Alt+←', NAV_FORWARD_KEY = 'Ctrl+Alt+→';

function Workspace({ user, google, onSignIn, onLogout }: { user: User; google: boolean; onSignIn: () => void; onLogout: () => void }) {
  // what the hash shows (one project, one file at a time): a document, "text:"/"pdf:" files, or
  // "raw:<document>" — the document beside its LaTeX source
  const [hashId, setHashId] = useState<string | null>(parseHash().id);
  const docId = hashId ? hashId.replace(/^raw:/, '') : null;
  const rawSplit = !!hashId && hashId.startsWith('raw:');
  useEffect(() => { restoreSidebarWidths(); }, []);
  // The writing area's panes — WYSIWYG, TeX source, PDF — any of them, in any order (app/panes.ts,
  // the PaneSwitch in the menu bar); kept per browser. "raw:<doc>" in the hash asks for the source.
  const [panes, setPanesState] = useState<PaneLayout>(loadLayout);
  const setPanes = (fn: (l: PaneLayout) => PaneLayout) => setPanesState(l => { const n = fn(l); if (n !== l) saveLayout(n); return n; });
  const narrowPanes = useMedia(NARROW_PANES);
  const prevHash = useRef<{ doc: string | null; raw: boolean }>({ doc: null, raw: false });
  useEffect(() => {
    const prev = prevHash.current;
    prevHash.current = { doc: docId, raw: rawSplit };
    if (!docId) return;
    if (rawSplit) setPanes(l => setPaneShown(l, 'tex', true));
    else if (prev.doc === docId && prev.raw) setPanes(l => setPaneShown(l, 'tex', false));   // the same document without "raw:" (Back, a switch)
  }, [hashId]);
  // the file shown: its project opens on it next time (lastfile.ts)
  useEffect(() => { if (docId) noteShown(docId); }, [docId]);
  // the layout as of now: keys and menus may run a handler from an earlier render (Preact installs the
  // new ones after the paint), and two quick Ctrl+Alt+S must toggle twice
  const panesRef = useRef(panes);
  panesRef.current = panes;
  /** A new pane layout (from the switch, a menu, a key); the source pane is mirrored in the hash ("raw:"), so Back undoes it. */
  const changePanes = (next: PaneLayout | ((l: PaneLayout) => PaneLayout)) => {
    const cur = panesRef.current;
    const n = typeof next === 'function' ? next(cur) : next;
    if (n === cur) return;
    panesRef.current = n;
    setPanes(() => n);
    const h = parseHash().id;
    const id = h ? h.replace(/^raw:/, '') : null;
    if (id && n.shown.tex !== h!.startsWith('raw:')) location.hash = '#/' + (n.shown.tex ? 'raw:' : '') + id;
  };
  /**
   * A link's view (`view=`, app/panes.ts): the panes and Pages as the sender had them, kept like a
   * switch of one's own. The address then says it no more: a reload keeps what was changed since.
   */
  const applyLinkView = (v: LinkView) => {
    const raw = location.hash.replace(/^#\/?/, '');
    const q = raw.indexOf('?');
    const idPart = (q >= 0 ? raw.slice(0, q) : raw).replace(/^raw:/, '');
    const params = new URLSearchParams(q >= 0 ? raw.slice(q + 1) : '');
    params.delete('view');
    const rest = params.toString().replace(/\+/g, '%20');
    history.replaceState(history.state, '', '#/' + (v.panes.includes('tex') ? 'raw:' : '') + idPart + (rest ? '?' + rest : ''));
    changePanes(l => (presetMatches(l, v.panes) ? l : applyPreset(l, v.panes)));
    if (v.panes.includes('doc')) {
      const id = decodeURIComponent(idPart);
      try { if (v.pages) localStorage.setItem(pagesKeyOf(id), '1'); else localStorage.removeItem(pagesKeyOf(id)); } catch { /* private window: for this visit */ }
      setPagesFor({ doc: id, on: v.pages });
    }
  };
  useEffect(() => { const v = parseHash().view; if (v) applyLinkView(v); }, []);
  const showPane = (id: PaneId, on = true) => changePanes(l => setPaneShown(l, id, on));
  const flipPane = (id: PaneId) => changePanes(l => togglePane(l, id));
  /** The Source switches (Ctrl+Alt+S, the right rail, the panel tabs, the View menu): the LaTeX source beside the document. */
  const toggleRawSplit = () => flipPane('tex');
  /** the panes on screen, left to right (one at a time on a phone-width screen) */
  const shownPanes = narrowPanes ? visiblePanes(panes).slice(0, 1) : visiblePanes(panes);
  /** CSS order places the panes (they stay mounted in one DOM order, so moving one never reloads the editor or the PDF); flex-grow is its width */
  const paneStyle = (id: PaneId) => ({ order: 2 * panes.order.indexOf(id), flex: `${paneGrow(panes, id, shownPanes)} 1 0px`, ...(shownPanes.includes(id) ? {} : { display: 'none' }) });
  // the PDF pane is mounted the first time it is shown and then kept (hidden), so showing it again does not reload the PDF
  const pdfMountedRef = useRef(false);
  if (panes.shown.pdf) pdfMountedRef.current = true;
  const pdfMounted = pdfMountedRef.current;
  // .tex and markdown documents open in the collaborative editor, other text files in a plain text
  // editor (ids prefixed with "text:"), a project's PDF files in the PDF viewer ("pdf:")
  const isTextTab = !!docId && docId.startsWith('text:');
  const isPdfTab = !!docId && docId.startsWith('pdf:');
  const textId = docId ? docId.replace(/^(text|pdf):/, '') : null;
  // an .html file is an article (core html/article.ts: written in this editor) or a deliverable; which,
  // the project's file list says (articlekinds.ts) — until it has, neither editor opens
  const isArticle = !!docId && !isTextTab && !isPdfTab && isArticleDoc(docId);
  const htmlPending = !!docId && !isTextTab && !isPdfTab && !htmlKindKnown(docId);
  const isLyxDoc = !!docId && !isTextTab && !isPdfTab && (docId.endsWith('.tex') || isMarkdownDoc(docId) || isArticle);
  const isBoardTab = !!docId && !isTextTab && !isPdfTab && docId.endsWith('.board');
  // an HTML deliverable (a deck, a poster, a web page): the deliverable editor (deliverable/)
  const isHtmlTab = !!docId && !isTextTab && !isPdfTab && /\.html?$/i.test(docId) && !isArticle && !htmlPending;
  // a bare project link (`#/owner/project`, no file): a brand-new project has nowhere else to send
  // you yet. Shown as a small landing in the editor area (its file tree is already the left sidebar)
  // instead of falling through to the plain-text editor, which would 404 trying to load it as a file.
  const isProjectRoot = !!docId && !isTextTab && !isPdfTab && docPathOf(textId!) === '';
  // the project shown in the documents panel (its owner gets the Share button)
  const [curProject, setCurProject] = useState<Project | null>(null);
  const [meta, setMeta] = useState<DocMeta | null>(null);
  const [headerLines, setHeaderLines] = useState<string[]>([]);
  const [status, setStatus] = useState<Status>({ connected: false, synced: false, users: [] });
  const [layout, setLayout] = useState('Standard');
  const [outline, setOutline] = useState<OutlineItem[]>([]);
  const [activePos, setActivePos] = useState(0);
  // Metadata (macros, bibliography, labels) follows the project's file changes: when an agent, a
  // git push or a collaborator edits macros.tex / *.bib, open editors learn it without a manual
  // "Reload metadata" (throttled — the events also fire for this document's own saves).
  const metaTimer = useRef<number | undefined>(undefined);
  const metaAt = useRef(0);
  useProjectEvents(isLyxDoc ? projectOfDoc(docId!) : null, () => {
    if (!docId) return;
    window.clearTimeout(metaTimer.current);
    metaTimer.current = window.setTimeout(() => {
      metaAt.current = Date.now();
      api.meta(docId).then(m => { setMeta(m); editorContext.meta = m; const v = editorRef.current?.view; if (v) refreshMacros(v, m.macros); }).catch(() => { /* transient */ });
    }, Math.max(2500, 20000 - (Date.now() - metaAt.current)));
  });
  // sidebars: the documents panel (left: project, its file tree with the documents' outlines) and the right
  // panels; shown / hidden state is kept per browser (a hidden sidebar leaves a rail to bring it back)
  // (a phone-width screen starts with the documents panel folded into its rail: the panel took 270 of an
  // iPhone's 393 pixels and left the text a column one letter wide)
  const [showFiles, setShowFiles] = useState(() => { const v = stored('ol.files'); return v !== null ? v !== '0' : !(typeof matchMedia === 'function' && matchMedia(DRAWER_LAYOUT_QUERY).matches); });
  const [rightTab, setRightTab] = useState<RightTab | null>(() => { const v = stored('ol.right'); return v !== null && (RIGHT_TABS as readonly string[]).includes(v) ? v as RightTab : null; });
  // Entering the phone/tablet drawer layout must expose the canvas and recovery controls.
  // This listens to the breakpoint, not each resize: a drawer opened there by hand stays open.
  useLayoutEffect(() => {
    if (typeof matchMedia !== 'function') return;
    return foldDrawersOnNarrow(matchMedia(DRAWER_LAYOUT_QUERY), () => {
      setShowFiles(false);
      setRightTab(null);
    });
  }, []);
  // the LaTeX source is a panel below the writing area with its own switch
  useEffect(() => { try { localStorage.setItem('ol.files', showFiles ? '1' : '0'); localStorage.setItem('ol.right', rightTab ?? ''); } catch { /* ignore */ } }, [showFiles, rightTab]);
  // dragged sidebar widths from the last visit (SidebarGrip)
  useEffect(() => {
    for (const side of ['left', 'right'] as const) {
      const v = stored('ol.' + side + 'w');
      if (v) document.documentElement.style.setProperty(`--${side}-width`, v);
    }
  }, []);
  const [pdf, setPdf] = useState<PdfState>(EMPTY_PDF);
  /** the save an automatic build was last started for (never twice for the same one, even if it failed) */
  const lastAutoFor = useRef(0);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [message, setMessage] = useState<{ text: string; kind: 'info' | 'error' } | null>(null);
  const [marginMode, setMarginModeState] = useState(localStorage.getItem('ol.margin') === '1');
  // View ▸ Pages (editor/plugins/pages.ts): the document on the last build's pages — per document
  // (`ol.pages:<doc>`): every document opens in the normal view until Pages is turned on for it
  const pagesKeyOf = (id: string | null) => 'ol.pages:' + (id ?? '');
  const readPages = (id: string | null) => { try { return !!id && localStorage.getItem(pagesKeyOf(id)) === '1'; } catch { return false; } };
  // (the switch belongs to its document: another one is never shown on pages until its own is read)
  const [pagesFor, setPagesFor] = useState<{ doc: string | null; on: boolean }>(() => { try { localStorage.removeItem('ol.pages'); } catch { /* (the old setting for all documents) */ } return { doc: docId, on: readPages(docId) }; });
  useEffect(() => { setPagesFor({ doc: docId, on: readPages(docId) }); }, [docId]);
  const pagesOn = pagesFor.doc === docId && pagesFor.on;
  const [combined, setCombined] = useState(localStorage.getItem('ol.combined') === '1');
  const [childIds, setChildIds] = useState<string[]>([]);
  /** whether this person's edits are tracked (Suggesting) … */
  const [tracking, setTracking] = useState(false);
  /** … and the document's own setting (\tracking_changes: everyone's, unless they chose for themselves) */
  const [docTracking, setDocTracking] = useState(false);
  const [chord, setChord] = useState<string | null>(null);
  const [changeInfo, setChangeInfo] = useState<string | null>(null);
  const [save, setSave] = useState<SaveState>({ state: 'connecting', pending: false, savedAt: 0, unavailable: false });
  const [reloadKey, setReloadKey] = useState(0);
  // A newer client build is deployed (checked a few seconds after the start, after every (re)connect
  // of a document, when the tab comes back to the foreground or online, and every 15 minutes), whatever
  // the tab shows: offer a reload; do it unasked only while the tab is hidden and nothing is half-done
  // (the text editor's edits and every other open editor's confirmed, no talk going on — update.ts
  // holdReload —, no dialog open). Only the text editor's connection used to start the checks, and its
  // save state to allow the reload: a tab left on slides kept a days-old build — one whose slide show
  // did not go along with the other screens' (22 Oct 2026).
  const [updateReady, setUpdateReady] = useState(false);
  useEffect(() => {
    if (updateReady) return;
    let alive = true;
    const check = () => { if (navigator.onLine !== false) void newerVersionAvailable().then(v => { if (alive && v) setUpdateReady(true); }); };
    const t = setTimeout(check, 3000);
    const iv = setInterval(check, 15 * 60000);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', check);
    return () => { alive = false; clearTimeout(t); clearInterval(iv); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('online', check); };
  }, [status.connected, updateReady]);
  useEffect(() => {
    if (!updateReady) return;
    const quietReload = () => {
      if (document.visibilityState !== 'hidden' || (isLyxDoc && (save.pending || save.state !== 'saved')) || reloadHeld() || document.querySelector('.dialog-backdrop')) return;
      location.reload();
    };
    quietReload();
    document.addEventListener('visibilitychange', quietReload);
    return () => document.removeEventListener('visibilitychange', quietReload);
  }, [updateReady, save.pending, save.state, isLyxDoc]);
  const [zoom, setZoom] = useState(Number(localStorage.getItem('ol.zoom') || 1));
  // width of the text column in px (0 = full width), see View ▸ Text width
  const [textWidth, setTextWidth] = useState<number>(() => { const v = Number(localStorage.getItem('ol.textWidth')); return Number.isFinite(v) && localStorage.getItem('ol.textWidth') !== null ? v : 720; });
  const stepTextWidth = (d: number) => setTextWidth(w => (d === 0 ? 720 : Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, (w || 1200) + d * 60))));
  // text size of notes and comments, in % of the document text (the ruler's − / + buttons in margin mode)
  const [noteScale, setNoteScale] = useState<number>(() => { const v = Number(localStorage.getItem('ol.noteScale')); return v >= NOTE_SCALE_MIN && v <= NOTE_SCALE_MAX ? v : NOTE_SCALE_DEFAULT; });
  useEffect(() => {
    document.documentElement.style.setProperty('--note-size', noteScale / 100 + 'em');
    localStorage.setItem('ol.noteScale', String(noteScale));
  }, [noteScale]);
  // Toolbars that come and go with the cursor (the math rows appear when a formula is entered)
  // move the page below them: keep what is on screen where it is by scrolling the same amount.
  const scrollRef = useRef<HTMLDivElement>(null);
  // margin mode splits the document pane: text | sash | notes & comments (app/notespane.tsx)
  // (toggleMargin is called on a click, by when it is defined below)
  const notesFold = useNotesFold(marginMode, () => toggleMargin());
  const notesPane = useNotesPane(scrollRef, marginMode && isLyxDoc, notesFold.fold);
  const scrollTop = useRef<number | null>(null);
  const scrollDoc = useRef<string | null>(null);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !el.getClientRects().length) { scrollTop.current = null; return; }   // gone, or its pane is hidden
    // another document: nothing on screen to keep in place
    if (scrollDoc.current !== docId) { scrollDoc.current = docId; scrollTop.current = null; }
    const top = el.getBoundingClientRect().top;
    // (at the very top the document's start stays in view: the rows a document brings when it
    // opens — a layout document's toolbar — must not scroll its first lines away)
    if (scrollTop.current !== null && top !== scrollTop.current && el.scrollTop > 0) el.scrollTop += top - scrollTop.current;
    scrollTop.current = top;
  });
  const [showRuler, setShowRuler] = useState(localStorage.getItem('ol.ruler') !== '0');
  useEffect(() => { localStorage.setItem('ol.ruler', showRuler ? '1' : '0'); }, [showRuler]);
  // Margin ink (drawing in the space beside the text): on tablets the toolbar activates itself.
  const [inkMode, setInkModeState] = useState(() => { const v = localStorage.getItem('ol.ink'); return v !== null ? v === '1' : isTabletClient(); });
  const setInkMode = (fn: (m: boolean) => boolean) => setInkModeState(m => { const next = fn(m); localStorage.setItem('ol.ink', next ? '1' : '0'); return next; });
  const [, inkTick] = useState(0);
  // (a pen that drew on a margin turns Draw on: editor/plugins/ink.ts onPenInMargin)
  useEffect(() => subscribeInk(() => { inkTick(t => t + 1); if (getInk().active) setInkMode(() => true); }), []);
  useEffect(() => { setInk({ active: inkMode }); }, [inkMode]);
  // entering ink mode adds wide gutters: keep the column centred (the snap point)
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !inkMode) return;
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
  }, [inkMode, docId]);
  const [findOpenState, setFindOpenState] = useState(false);
  const findOpen = findOpenState;
  // Ctrl+F (again): the find field gets the keyboard — `autofocus` does nothing for an input added later,
  // so the typed search went into the document
  const findInput = useRef<HTMLInputElement>(null);
  const [findFocus, setFindFocus] = useState(0);
  const setFindOpen = (v: boolean) => { setFindOpenState(v); if (v) setFindFocus(n => n + 1); };
  useEffect(() => {
    if (!findOpen) return;
    const t = setTimeout(() => { findInput.current?.focus(); findInput.current?.select(); }, 0);
    return () => clearTimeout(t);
  }, [findOpen, findFocus]);
  // sharing: the project whose share dialog is open; view-only when the current project was shared for viewing
  const [shareFor, setShareFor] = useState<string | null>(null);
  // "Copy link to …": who may open it, asked first (app/CopyLinkDialog.tsx)
  const [linkAsk, setLinkAsk] = useState<{ url: string; what: string } | null>(null);
  // File ▸ New slides / poster / page…: the project a layout document is created in
  const [newLayoutFor, setNewLayoutFor] = useState<string | null>(null);
  // the project whose git dialog (clone URL, tokens, history) is open
  const [gitFor, setGitFor] = useState<string | null>(null);
  /** the interactive walkthrough: offered once per browser, restartable from Help */
  // guests came for somebody's document, not for a tour (it is offered once they have an account)
  // (and not over an import that is about to run — Home.tsx picks it up; the tour is offered on the next visit)
  const [tour, setTour] = useState<'intro' | 'steps' | null>(() => (!user.guest && tourWanted() && !pendingImportFlag() ? 'intro' : null));
  const [tourRequest, setTourRequest] = useState(0);
  // Every explicit opening is a new walkthrough, including Help while one is open.
  const openTour = useCallback((mode: 'intro' | 'steps') => {
    setTour(mode);
    setTourRequest(n => n + 1);
  }, []);
  // a new account arriving with an Overleaf import skips the welcome card for now, but still picks its interface
  const [askInterface, setAskInterface] = useState(() => !user.guest && tourWanted() && pendingImportFlag());
  const [viewOnly, setViewOnly] = useState(false);
  // the mode switch's Viewing: this browser's editors read-only although the user may edit (per document)
  const [viewing, setViewing] = useState(false);
  const viewingRef = useRef(false); viewingRef.current = viewing;
  // the editor has its role from the metadata (until then it is read-only anyway)
  const metaApplied = useRef(false);
  // LyX toolbars: standard / extra always (unless hidden), math / table / review on, off or automatic (LyX's "auto")
  const { pref: themePref } = useTheme();
  const ui = useUiMode();
  // the account's interface (chosen in another browser) once its settings arrive
  useEffect(() => {
    if (user.guest) return;
    let current = true;
    const choice = uiChoiceVersion();
    api.settings().then(r => {
      if (current && choice === uiChoiceVersion()) adoptAccountUi(r.settings.interface);
    }).catch(() => undefined);
    return () => { current = false; };
  }, [user.username, user.guest]);
  usePresentation();   // View ▸ Presentation mode: Shift+F11 toggles, Esc leaves
  // per-browser preferences (spell checking, AI assistance) and whether the server can answer AI requests
  const [prefs, setPrefsState] = useState<Prefs>(getPrefs);
  useEffect(() => subscribePrefs(setPrefsState), []);
  /** The Agent panel appears once AI assistance is activated in the settings (any AI toggle, or the ✦ button). */
  const aiActivated = prefs.aiButton || prefs.aiRewrite || prefs.aiCompleteText || prefs.aiCompleteMath;
  /** the account has agents connected from elsewhere over MCP (Claude Code, Codex …): the Agent panel talks to them too */
  const [hasExternalAgents, setHasExternalAgents] = useState(false);
  useEffect(() => {
    if (user?.guest) return;
    let last = 0;
    // again when the page comes back to the front (an agent was just started in a terminal)
    const check = () => { if (Date.now() - last < 5000 || document.hidden) return; last = Date.now(); api.externalAgents().then(r => setHasExternalAgents(r.agents.length > 0)).catch(() => { /* none */ }); };
    check();
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => { window.removeEventListener('focus', check); document.removeEventListener('visibilitychange', check); };
  }, [user?.id]);
  const agentTab = aiActivated || hasExternalAgents;
  useEffect(() => { if (!agentTab && rightTab === 'agent') setRightTab(null); }, [agentTab, rightTab]);
  // "Ask agent about this" in the editor's right-click menu: the selection pinned for the Agent panel's next message
  useEffect(() => {
    if (!agentTab) return;
    editorContext.askAgent = () => { setRightTab('agent'); askAgentAbout(); };
    return () => { editorContext.askAgent = undefined; };
  }, [agentTab]);
  const [ai, setAi] = useState<AiStatus | null>(null);
  // completions in flight (a small indicator in the status bar; several may overlap briefly)
  const [aiBusy, setAiBusy] = useState(0);
  useEffect(() => { editorContext.aiBusy = (on) => setAiBusy(n => Math.max(0, n + (on ? 1 : -1))); return () => { editorContext.aiBusy = undefined; }; }, []);
  useEffect(() => { api.aiStatus().then(s => { editorContext.ai = s; setAi(s); }).catch(() => { const s = { available: false, model: '', completionModel: '', models: [] }; editorContext.ai = s; setAi(s); }); }, []);
  const [toolbars, setToolbars] = useState<ToolbarPrefs>(loadToolbarPrefs);
  useEffect(() => { localStorage.setItem('ol.toolbars', JSON.stringify(toolbars)); }, [toolbars]);
  const setToolbar = (id: ToolbarId, mode: ToolbarMode) => setToolbars(t => ({ ...t, [id]: mode }));
  const tbMode = (id: ToolbarId): ToolbarMode => toolbars[id] ?? 'auto';
  // the formula being edited (LyX shows the math toolbar while the cursor is in math)
  const [mathField, setMathField] = useState<LyxMathField | null>(null);
  useEffect(() => { const l = (f: LyxMathField | null) => { setMathField(f); editorContext.mathField = f; }; mathFocusListeners.add(l); return () => { mathFocusListeners.delete(l); }; }, []);
  // cursor moves inside a formula count as selection changes (source pane, status bar)
  useEffect(() => { const l = () => setSelTick(t => t + 1); mathCursorListeners.add(l); return () => { mathCursorListeners.delete(l); }; }, []);
  const [findQ, setFindQ] = useState(''), [replQ, setReplQ] = useState('');
  const [findCase, setFindCase] = useState(false), [findWord, setFindWord] = useState(false);
  const [findRegex, setFindRegex] = useState(false), [findMath, setFindMath] = useState(false), [findSel, setFindSel] = useState(false);
  const [findAdv, setFindAdv] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // the documents panel reports the project (and so the Share button); while it is hidden (closed,
  // folded beside the PDF, a phone's drawer) the open document's project is looked up here instead
  const docProject = docId ? projectOfDoc(docId.replace(/^(text|pdf):/, '')) : null;
  useEffect(() => {
    if (showFiles || !docProject || curProject?.name === docProject) return;
    let live = true;
    api.projects().then(r => { if (live) setCurProject(r.projects.find(p => p.name === docProject) ?? null); }).catch(() => {});
    return () => { live = false; };
  }, [showFiles, docProject, curProject?.name, refreshKey]);
  // the project's people's pictures beside their comments (shellutil applyCommentAvatars)
  useEffect(() => { applyCommentAvatars([user, curProject?.owner, ...(curProject?.members ?? [])]); }, [user, curProject]);
  const [selVersion, setSelVersion] = useState(0);
  const [docTick, setDocTick] = useState(0);
  const [selTick, setSelTick] = useState(0);
  const editorRef = useRef<EditorHandle | null>(null);
  const childRefs = useRef(new Map<string, EditorHandle>());
  const activeViewRef = useRef<EditorView | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pendingGoto = useRef<string | null>(parseHash().goto);
  const pendingHeading = useRef<number | null>(parseHash().heading);
  /** the place a link names (editor/permalink.ts): a document goes to it once it has its text (runGoto), the other editors are handed it */
  const pendingPlace = useRef<Place | null>(parseHash().place);
  const [place, setPlace] = useState<Place | null>(() => parseHash().place);
  const [, force] = useState(0);
  const rerender = () => force(x => x + 1);
  // an .html file's kind comes with its project's file list: the right editor opens then
  useEffect(() => onArticleKinds(rerender), []);
  useEffect(() => {
    if (!htmlPending || !docId) return;
    let live = true;
    void api.projects().then(() => { if (live && !htmlKindKnown(docId)) htmlKindsUnsure(); }, () => { if (live) htmlKindsUnsure(); });
    return () => { live = false; };
  }, [htmlPending, docId]);

  const notify = useCallback((text: string, kind: 'info' | 'error' = 'info') => {
    setMessage({ text, kind });
    setTimeout(() => setMessage(m => (m?.text === text ? null : m)), 4000);
    if (kind === 'error') recordUsage('notice', noticeTemplate(text));   // what went wrong, as a template (usage.ts)
  }, []);

  useEffect(() => {
    const onHash = () => {
      const h = parseHash();
      // a document of a project the server did not list may be named by an old id: its current id
      // replaces it first (and comes back through hashchange); nothing opens under the old one
      if (h.id && !knownProjects.has(projectOfDoc(h.id.replace(/^(raw|text|pdf):/, '')))) { void canonicalHash().then(moved => { if (!moved && parseHash().id === h.id) apply(parseHash()); }); return; }
      apply(h);
    };
    const apply = (h: ReturnType<typeof parseHash>) => {
      pendingGoto.current = h.goto; pendingHeading.current = h.heading; pendingPlace.current = h.place; setPlace(h.place); setHashId(h.id);
      if (h.view) applyLinkView(h.view);
      if (h.id?.startsWith('text:')) navHistory.visit(h.id, null);
      if ((h.goto || h.heading !== null || h.place?.text) && h.id === editorContext.docId) runGoto();
    };
    navHistory.load();
    const h0 = parseHash().id;
    if (h0?.startsWith('text:')) navHistory.visit(h0, null);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  useEffect(() => navHistory.subscribe(rerender), []);
  // a share link (#/<owner>/<project>/<path>?share=<token>): join the project, then open the linked file (or its main document);
  // a public project's address puts it on the project list
  useEffect(() => {
    const check = () => {
      const { share: token, sharePath } = parseHash();
      if (!token) { void visitPublic().then(v => { if (v?.public && v.role === 'view') setRefreshKey(k => k + 1); }); return; }
      api.acceptShare(token, sharePath).then(r => {
        notify(`You can now ${r.role === 'view' ? 'view' : 'edit'} “${r.title ?? projectShortName(r.project)}”`);
        setRefreshKey(k => k + 1);
        location.hash = shareLanding(r);
      }).catch(e => { notify((e as Error).message, 'error'); location.hash = ''; });
    };
    check();
    window.addEventListener('hashchange', check);
    return () => window.removeEventListener('hashchange', check);
  }, []);

  useEffect(() => applyEditorZoom(zoom), [zoom]);
  useDocumentViewportZoom(scrollRef, docId);
  // Settings ▸ Editor ▸ Font ▸ "As in the document" follows the document's roman font
  useEffect(() => { setDocumentFonts(headerLines); setDocumentParagraphs(headerLines); setDocumentHeadings(headerLines); setDocumentNumbering(headerLines); setLayoutHeader(headerLines); }, [headerLines]);
  // Ctrl/Cmd +/- zoom the document text, never the browser chrome — wherever the focus is (formula
  // fields, panels), unless a pane that zooms itself was pressed on last (the PDF, app/zoomkeys.ts).
  // Ctrl+0 is a paragraph style now (Part, like LyX's Alt+P 0); reset via the status bar.
  useEffect(() => setDefaultZoom(k => editorContext.ui?.zoom(k)), []);
  // A file dropped outside a drop target (editor text, file browser) must not make the browser
  // navigate to it — that would throw the whole workspace away. Element handlers run first.
  useEffect(() => {
    const over = (ev: DragEvent) => { if (ev.dataTransfer?.types.includes('Files')) ev.preventDefault(); };
    const drop = (ev: DragEvent) => { if (ev.dataTransfer?.types.includes('Files')) ev.preventDefault(); };
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => { window.removeEventListener('dragover', over); window.removeEventListener('drop', drop); };
  }, []);
  // Ctrl/Cmd+R starts the PDF build wherever the focus is (a panel, a dialog, a comment box):
  // the editor's keymap only sees the key with the cursor in the text, and the browser's reload
  // silently threw away the tour and the interface state. Ctrl+Shift+R stays the browser's.
  useEffect(() => {
    if (!docId) return;
    const onKey = (ev: KeyboardEvent) => {
      if ((!ev.ctrlKey && !ev.metaKey) || ev.altKey || ev.shiftKey || (ev.key !== 'r' && ev.key !== 'R')) return;
      ev.preventDefault(); ev.stopPropagation();
      editorContext.ui?.viewPdf?.();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [docId]);
  // A new text width reflows the whole document: keep the cursor where it is on screen (the ruler
  // is dragged with the eyes on the text) by scrolling by the amount the cursor moved.
  useEffect(() => {
    const v = activeViewRef.current;
    const scroller = scrollRef.current;
    const cursorTop = () => { try { return v && v.dom.isConnected ? v.coordsAtPos(v.state.selection.from).top : null; } catch { return null; } };
    const before = cursorTop();
    document.documentElement.style.setProperty('--text-width', textWidth > 0 ? textWidth + 'px' : '100%');
    localStorage.setItem('ol.textWidth', String(textWidth));
    const after = cursorTop();
    if (scroller && before !== null && after !== null && after !== before) scroller.scrollTop += after - before;
  }, [textWidth]);
  useEffect(() => { editorContext.combined = combined; localStorage.setItem('ol.combined', combined ? '1' : '0'); }, [combined]);

  /** show a document or file (in place — one project, one file at a time), optionally at a label or the n-th heading */
  const openInTab = useCallback((id: string, opts: { goto?: string; heading?: number } = {}) => {
    const target = '#/' + id + (opts.goto ? '?goto=' + encodeURIComponent(opts.goto) : opts.heading !== undefined ? '?heading=' + opts.heading : '');
    if (location.hash === target || (location.hash === '#/' + id && !opts.goto && opts.heading === undefined)) { pendingGoto.current = opts.goto ?? null; pendingHeading.current = opts.heading ?? null; runGoto(); }
    else location.hash = target;
  }, []);
  /** leave the document: back to the start screen */
  const closeDoc = useCallback(() => { location.hash = '#/'; }, []);

  /** Find a label in a view and select it. */
  const gotoLabelIn = (view: EditorView, name: string): boolean => {
    let found = -1;
    view.state.doc.descendants((node, pos) => {
      if (found >= 0) return false;
      if (node.type.name === 'command' && node.attrs.cmd === 'label' && unquote(commandParams(node).get('name')) === name) found = pos;
      if (node.type.name === 'math_display' && String(node.attrs.latex).includes(`\\label{${name}}`)) found = pos;
      return true;
    });
    if (found < 0) return false;
    view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, found)).scrollIntoView());
    view.focus();
    const dom = view.nodeDOM(found) as HTMLElement | null;
    dom?.scrollIntoView?.({ block: 'center' });
    return true;
  };
  const gotoLabel = useCallback((name: string, from?: EditorView) => {
    const views = [from, editorRef.current?.view, ...[...childRefs.current.values()].map(h => h.view)].filter((v): v is EditorView => !!v);
    if (navHistory.jump(() => views.some(v => gotoLabelIn(v, name)))) return;
    const l = editorContext.meta?.labels.find(x => x.name === name);
    if (l?.file && editorContext.project) { openInTab(`${editorContext.project}/${l.file}`, { goto: name }); return; }
    notify(`Label “${name}” not found`, 'error');
  }, []);
  const runGoto = () => {
    const v = editorRef.current?.view;
    const name = pendingGoto.current;
    if (name && v && gotoLabelIn(v, name)) pendingGoto.current = null;
    // ?heading=<n>: the n-th heading (the documents panel's static outline counts them the same way)
    const n = pendingHeading.current;
    if (n !== null && v) {
      const items = buildOutline(v.state.doc, false).filter(i => sectionLevel(i.layout) !== null);
      const it = items[n];
      if (it) { jumpToOutline(v, it); pendingHeading.current = null; }
    }
    // ?text=…: the quoted place (editor/permalink.ts)
    const p = pendingPlace.current;
    if (p?.text && v) { pendingPlace.current = null; navHistory.jump(() => showPlace(v, p)); }
  };
  /** the cursor to a heading of the outline (the Navigate menu, the documents panel) */
  const jumpToOutline = (v: EditorView, it: OutlineItem) => {
    v.focus();   // before the dispatch: scrollIntoView follows the DOM selection, which must be in the editor
    const tr = v.state.tr.setSelection(TextSelection.near(v.state.doc.resolve(Math.min(it.pos + 1, v.state.doc.content.size)))).scrollIntoView();
    navHistory.jump(() => v.dispatch(tr));
    (v.nodeDOM(it.pos) as HTMLElement | null)?.scrollIntoView?.({ block: 'start' });
  };

  const onSelection = (view: EditorView, info?: { docChanged: boolean }) => {
    activeViewRef.current = view; editorContext.activeView = view;
    navHistory.visitState(viewDocId(view), view.state, { docChanged: !!info?.docChanged });
    const p = C.currentParagraph(view.state);
    setLayout(p ? p.node.attrs.layout : '');
    if (view === editorRef.current?.view) setActivePos(view.state.selection.from);
    setChord(chordKey.getState(view.state) ?? null);
    const ch = changeAt(view.state, view.state.selection.from);
    setChangeInfo(ch ? describeChange(ch.type, ch.author, ch.time) : null);
    setSelTick(t => t + 1);
    rerender();
  };

  /** Presence avatar clicked: jump to where that user is editing (in the master or a child editor). */
  const jumpToUser = (u: PresenceUser) => {
    if (u.self) { const v = editorRef.current?.view; if (v) { v.focus(); v.dispatch(v.state.tr.scrollIntoView()); } return; }
    const handles = [editorRef.current, ...childRefs.current.values()].filter((h): h is EditorHandle => !!h);
    const hit = navHistory.jump(() => handles.find(h => h.gotoUser(u.clientId)));
    if (hit) notify(`Jumped to ${u.name}'s cursor`);
    else notify(`“${u.name}” has no cursor in this document (yet)`, 'error');
  };

  /**
   * Navigation history (navhistory.ts): Back / Forward show an earlier place — in an open editor
   * (the master or a child of the combined view) right away, else by switching to that document's
   * tab, where the editor puts the cursor there once the document is loaded (`initialCursor`).
   */
  const pendingNav = useRef<NavLocation | null>(null);
  const showLocation = (loc: NavLocation) => {
    if (loc.docId.startsWith('text:')) { navHistory.restored(); if (parseHash().id !== loc.docId) location.hash = '#/' + loc.docId; return; }
    const handles = [editorRef.current, ...childRefs.current.values()].filter((h): h is EditorHandle => !!h);
    const h = handles.find(x => viewDocId(x.view) === loc.docId);
    if (h) {
      const v = h.view;
      const doc = v.state.doc;
      const pos = loc.cursor ? restoredCursorPos(doc, loc.cursor) : 0;
      try { v.dispatch(v.state.tr.setSelection(TextSelection.near(doc.resolve(Math.min(pos, doc.content.size)))).scrollIntoView().setMeta('addToHistory', false)); } catch { /* not a valid place any more */ }
      navHistory.restored();
      v.focus();
      return;
    }
    pendingNav.current = loc;
    if (parseHash().id !== loc.docId) location.hash = '#/' + loc.docId;
  };
  const navBack = useCallback(() => { const loc = navHistory.back(); if (loc) showLocation(loc); else notify('Nothing to go back to'); }, []);
  const navForward = useCallback(() => { const loc = navHistory.forward(); if (loc) showLocation(loc); else notify('Nothing to go forward to'); }, []);
  // Ctrl+Alt+← / Ctrl+Alt+→ (⌥⌘← / ⌥⌘→) wherever the focus is (formula fields, panels, text-file tabs);
  // a rebound command is run by the palette's listener, which marks the event handled (defaultPrevented)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const k = keyFromEvent(e);
      if (!k) return;
      const action = k === canonical(effectiveShortcut(NAV_BACK_ID, NAV_BACK_KEY)) ? navBack : k === canonical(effectiveShortcut(NAV_FORWARD_ID, NAV_FORWARD_KEY)) ? navForward : null;
      if (!action) return;
      e.preventDefault(); e.stopImmediatePropagation();
      action();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  // create / destroy the editor when the document changes (metadata first, so formulas render once with the right macros)
  useEffect(() => {
    editorRef.current?.destroy();
    editorRef.current = null; activeViewRef.current = null;
    setMeta(null); setOutline([]); setChildIds([]); setPdf(EMPTY_PDF); lastAutoFor.current = 0;
    setDialog(null);   // a dialog belongs to the document it was opened in
    if (!docId || !containerRef.current) return;
    containerRef.current.innerHTML = '';
    editorContext.user = user; editorContext.docId = docId; editorContext.project = projectOfDoc(docId); editorContext.meta = null;
    editorContext.docDir = docDirOf(docId);
    // never carry the previous document's author id / tracking state over (changes would be mis-attributed)
    editorContext.changeAuthorId = undefined; editorContext.trackChanges = false;
    setViewing(false); viewingRef.current = false; metaApplied.current = false;
    let cancelled = false;
    let loadMeta: () => void = () => {};
    const scheduleOutline = debounce((view: EditorView) => { setOutline(buildOutline(view.state.doc, true, editorContext.meta?.secnumdepth ?? 3)); setChildIds(collectChildren(view)); }, 300);
    setSave({ state: 'connecting', pending: false, savedAt: 0, unavailable: false });
    // The editor loads its local copy and connects right away; the metadata request runs in
    // parallel. Until it has arrived the editor is read-only and formulas only show their source
    // (their macros are not known yet), so nothing is rendered twice.
    const handle: EditorHandle = createEditor({
      docId, user, container: containerRef.current, marginMode, readOnly: true,
      onStatus: setStatus,
      onSaveState: (st) => { if (!cancelled) setSave(st); },
      onSelectionChange: onSelection,
      initialCursor: () => { const p = pendingNav.current; if (p && p.docId === docId) { pendingNav.current = null; return p.cursor; } return null; },
      onDocChange: (view) => { setDocTick(t => t + 1); scheduleOutline(view); },
      onStale: (info) => { void resolveStale(handle, docId, info.pendingLocal); },
      // access revoked or role changed: the metadata tells the new role (or 403 → the tab closes)
      onAccessChanged: () => loadMeta(),
      onGone: (reason) => {
        if (cancelled) return;
        if (/reset/.test(reason)) return;   // handled by the epoch / stale-history flow
        // deleted in the explorer here: no alarm; elsewhere: say so. Either way the tab goes on to
        // another document of the folder (the start screen only when the project has none left)
        const mine = deletedHere.delete(docId);
        if (!mine) notify(`${docId}: ${reason} — the document was closed (a copy is in the project's versions)`, 'error');
        const key = projectOfDoc(docId);
        void api.projects().then(r => {
          if (cancelled || editorContext.docId !== docId) return;
          const p = r.projects.find(x => x.name === key);
          const next = p ? siblingDocument(p, docId) : null;
          if (next) location.hash = '#/' + next; else closeDoc();
        }).catch(() => { if (!cancelled) closeDoc(); });
      },
    });
    editorRef.current = handle; activeViewRef.current = handle.view; editorContext.activeView = handle.view;
    // header lines live in the Y meta map
    const metaMap = handle.ydoc.getMap<string>('meta');
    // the document's change tracking, and what applies to this person (EditModeSwitch: Only me / Everyone)
    const followDocTracking = (doc: boolean) => {
      const on = suggestingFor(docId, doc);
      setDocTracking(doc); setTracking(on); editorContext.trackChanges = on;
    };
    const readHeader = () => {
      try {
        const lines: string[] = JSON.parse(metaMap.get('header') ?? '[]');
        setHeaderLines(lines);
        // switched for everyone, here or by a co-author: followed at once
        const t = lines.find(l => l.startsWith('\\tracking_changes '));
        if (t) followDocTracking(t === '\\tracking_changes true');
      } catch { /* ignore */ }
    };
    readHeader(); metaMap.observe(readHeader);
    const h = handle;
    let firstSync = true;
    h.provider.on('sync', () => {
      if (firstSync) { firstSync = false; navHistory.visitState(docId, h.view.state); refreshLayoutCheck(h.view, () => api.layoutCheck(docId)); }
      setOutline(buildOutline(h.view.state.doc, true, editorContext.meta?.secnumdepth ?? 3));
      setChildIds(collectChildren(h.view));
      setDocTick(x => x + 1);
      setTimeout(runGoto, 50);
    });
    const withMeta = (m: DocMeta | null) => {
      if (cancelled) return;
      // an HTML article: the page's look (its stylesheet's custom properties) on the editor
      applyArticleLook(h.view, isArticleDoc(docId) ? m?.article?.vars ?? {} : null);
      if (m) {
        setMeta(m); editorContext.meta = m;
        // the browser's spell checker picks its dictionary from the lang attribute
        h.view.dom.lang = bcp47(m.language);
        applyAuthorColors(m.authors);
        followDocTracking(m.trackingChanges);
        editorContext.changeAuthorId = m.authors.find(x => x.name === user.name)?.id;
      }
      refreshMacros(h.view, m?.macros ?? null);
      const ro = m?.role === 'view';
      setViewOnly(ro);
      h.setViewOnly(ro || viewingRef.current);
      h.setEditable(!ro && !viewingRef.current);
      metaApplied.current = true;
      setOutline(buildOutline(h.view.state.doc, true, m?.secnumdepth ?? 3));
      rerender();
    };
    // an author unknown to the change bars (a co-author started suggesting after we loaded): the table again
    editorContext.refreshAuthors = () => { api.meta(docId).then(m => { if (cancelled) return; setMeta(m); editorContext.meta = m; applyAuthorColors(m.authors); }).catch(() => {}); };
    let metaRetry: ReturnType<typeof setTimeout> | undefined;
    let metaAttempt = 0;
    loadMeta = () => {
      clearTimeout(metaRetry);
      api.meta(docId).then(m => {
        metaAttempt = 0;
        // a .tex file that is not a document (a preamble / macro file opened by URL) belongs to the text editor
        const rel = docPathOf(docId);
        const entry = m.files?.find(f => f.path === rel);
        if (entry && entry.kind === 'tex') { location.hash = '#/text:' + docId; return; }
        withMeta(m);
      }).catch((e: Error & { status?: number }) => {
        if (cancelled) return;
        if (e.status === 403) { notify(e.message || 'You no longer have access to this project', 'error'); closeDoc(); return; }
        if (!navigator.onLine) { notify('Offline: document metadata (macros, bibliography) not available', 'error'); withMeta(null); return; }
        // online, but the server could not deliver the metadata: without it tracked changes could not be
        // attributed and macros would not render — keep the document read-only and retry
        metaAttempt++;
        const delay = Math.min(30000, 2000 * 2 ** Math.min(metaAttempt - 1, 4));
        notify(`Could not load document metadata: ${e.message}${metaAttempt <= 8 ? ' — read-only until it loads, retrying…' : ' — read-only; reload the page to try again'}`, 'error');
        h.setEditable(false);
        if (metaAttempt <= 8) metaRetry = setTimeout(loadMeta, delay);
      });
    };
    loadMeta();
    // an offline blip (laptop sleep) leaves the metadata unloaded: fetch it as soon as we are back
    window.addEventListener('online', loadMeta);
    rerender();
    return () => { cancelled = true; clearTimeout(metaRetry); window.removeEventListener('online', loadMeta); handle.destroy(); editorRef.current = null; };
  }, [docId, reloadKey]);

  // Viewing (the mode switch) on or off: the master and the combined view's children follow
  useEffect(() => {
    const h = editorRef.current;
    if (!h || !metaApplied.current) return;
    const ro = viewOnly || viewing;
    for (const x of [h, ...childRefs.current.values()]) { x.setViewOnly(ro); x.setEditable(!ro); }
  }, [viewing]);

  /**
   * The server's copy of the document has a different history than our local copy (the server
   * re-created the document, e.g. after its database was reset). Yjs cannot merge unrelated
   * histories, so: keep any unsynced local edits as a version on the server (they can be compared
   * and restored from the Versions panel), drop the local copy and load the server's document.
   */
  const resolveStale = async (h: EditorHandle, id: string, pendingLocal: boolean) => {
    let kept = '';
    if (pendingLocal) {
      const name = `offline changes by ${user.name} (not merged)`;
      try {
        const lyx = generateLyx({ view: h.view, ydoc: h.ydoc, docId: id });
        await api.createVersion(id, name, lyx);
        kept = `Your unsynced edits were kept as the version “${name}” — open Versions to compare or restore them.`;
      } catch {
        try {
          const lyx = generateLyx({ view: h.view, ydoc: h.ydoc, docId: id });
          const a = document.createElement('a');
          a.href = URL.createObjectURL(new Blob([lyx], { type: 'application/x-lyx' }));
          a.download = (id.split('/').pop() ?? 'document.tex').replace(/\.tex$/, '') + '-offline-changes.lyx';
          a.click();
          kept = 'Your unsynced edits could not be stored on the server; they were downloaded as a .lyx file instead (import it into the project to recover them).';
        } catch { notify('Your unsynced edits could not be exported. This local copy has been kept; copy your edits before reloading.', 'error'); return; }
      }
    }
    await h.discardLocal();
    await uiAlert('Document Replaced', `The document on the server was re-created while this copy was open, so the local copy cannot be merged and will be reloaded.${kept ? '\n\n' + kept : ''}`);
    setReloadKey(k => k + 1);
  };

  // UI hooks for keymap / node views
  useEffect(() => {
    editorContext.notify = notify;
    editorContext.openDialog = (name, arg) => setDialog({ name, arg });
    editorContext.openInsetDialog = (_view, pos) => { if (pos !== undefined) setDialog({ name: 'inset', arg: pos }); };
    editorContext.openInTab = openInTab;
    editorContext.gotoLabel = gotoLabel;
    editorContext.webLink = webAppLink;
    editorContext.askLinkScope = (url, what) => setLinkAsk({ url, what });
    editorContext.linkView = id => (isLyxDoc && (id === docId || childIds.includes(id)) ? viewParam(panes, pagesOn, viewKindOf(id)) : null);
    (window as any).overlyx = editorContext;   // handy for tests / debugging
    editorContext.ui = {
      save: () => {
        if (!docId) return;
        const st = editorRef.current?.saveState();
        if (st?.state === 'offline') { notify('You are offline — your changes are kept on this device and will be saved automatically when the connection is back'); return; }
        api.save(docId).then(() => notify('All changes are saved automatically — written to ' + docId.split('/').pop())).catch(e => notify(String(e.message), 'error'));
      },
      viewPdf: () => build(),
      syncToPdf: () => { void syncToPdf(); },
      updatePdf: () => build(),
      find: () => setFindOpen(true),
      openDialog: (name, arg) => setDialog({ name, arg }),
      toggleTrackChanges: () => toggleTracking(),
      toggleOutline: () => setShowFiles(s => !s),
      toggleSource: () => toggleRawSplit(),
      toggleCombined: () => setCombined(c => !c),
      acceptAll: () => run(acceptAllChanges()),
      rejectAll: () => run(rejectAllChanges()),
      closeTab: () => closeDoc(),
      zoom: (d) => { if (!layoutZoomStep(d)) setZoom(z => stepTextZoom(z, d)); },
      textWidth: stepTextWidth,
      openFile: () => setShowFiles(true),
      // any kind of file: the name's ending decides (app/newfile.ts)
      newFile: () => {
        const p = textId ? projectOfDoc(textId) : null;
        if (!p) return;
        void promptNewFile(p).then(id => { if (id) { location.hash = '#/' + id; setRefreshKey(k => k + 1); } });
      },
    };
  });

  const view = activeViewRef.current ?? editorRef.current?.view ?? null;
  const masterView = editorRef.current?.view ?? null;
  const run = (cmd: (state: any, dispatch: any, view?: any) => boolean) => { const v = activeViewRef.current ?? editorRef.current?.view; if (!v) return; cmd(v.state, v.dispatch, v); v.focus(); };
  const runView = (fn: (v: EditorView) => boolean) => { const v = activeViewRef.current ?? editorRef.current?.view; if (!v) return; fn(v); };

  // PDF builds are background jobs on the server: start one, then poll its status (also picks up
  // a build that is already running for this document, e.g. started from another tab)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollBuild = useCallback((id: string, announce: boolean) => {
    if (pollRef.current) clearTimeout(pollRef.current);
    const step = async () => {
      if (editorContext.docId !== id) return;
      let r: Awaited<ReturnType<typeof api.build>>;
      try { r = await api.build(id); } catch { pollRef.current = setTimeout(step, 3000); return; }
      if (editorContext.docId !== id) return;
      setPdf(p => ({ ...stateFromBuild(p, r), known: true }));
      if (jobActive(r.job)) { pollRef.current = setTimeout(step, 1000); return; }
      if (announce && r.job) notify(r.job.status === 'ok' ? 'PDF built' : r.job.status === 'cancelled' ? 'PDF build cancelled' : 'PDF build failed — see log', r.job.status === 'ok' ? 'info' : 'error');
    };
    void step();
  }, []);
  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);
  // when a document opens: show its last PDF, and resume polling if a build is running
  useEffect(() => { if (docId && !isProjectRoot) pollBuild(docId, false); }, [docId, isProjectRoot]);
  // a layout document: each new PDF brings the check of its text boxes against it (editor/layout/controller.ts)
  useEffect(() => { const v = editorRef.current?.view; if (v && docId && pdf.url) refreshLayoutCheck(v, () => api.layoutCheck(docId)); }, [docId, pdf.url]);

  /** Build the PDF. `open: false` (LyX's Update) leaves the PDF pane as it is; `auto`: started by the auto-build setting (quiet). */
  const build = async (opts: { open?: boolean; auto?: boolean } = {}) => {
    if (!docId) return;
    if (opts.open !== false) showPane('pdf');
    setPdf(p => ({ ...p, busy: true, auto: !!opts.auto }));
    try {
      const r = await api.export(docId, 'pdf', { auto: opts.auto });
      setPdf(p => ({ ...p, busy: true, job: r.job ?? p.job }));
      pollBuild(docId, !opts.auto);
    } catch (e) {
      setPdf(p => ({ ...p, busy: false, ok: false, log: String((e as Error).message) }));
      if (!opts.auto) notify('Could not start the PDF build: ' + String((e as Error).message), 'error');
    }
  };
  // Auto-build (Settings ▸ Editor ▸ PDF, or the ▾ beside View PDF): a moment after the document was
  // saved, when the PDF is older than that save — while the PDF pane is shown, or always. Never
  // while a build runs (when it is done and the document is still newer, this asks again), and
  // never twice for the same save (a failing document is not rebuilt in a loop).
  const pdfPaneVisible = isLyxDoc && panes.shown.pdf && (!narrowPanes || visiblePanes(panes)[0] === 'pdf');
  useEffect(() => {
    const mode = prefs.autoBuild;
    // (the Pages view shows the PDF's page breaks: it keeps the PDF up to date like the PDF pane does)
    // (an article's pane is the page itself, not its PDF: printed only when asked for)
    if (!docId || !isLyxDoc || isArticle || mode === 'off' || (mode === 'shown' && !pdfPaneVisible && !pagesOn)) return;
    if (!pdf.known || pdf.busy) return;
    const saved = save.savedAt;
    if (!saved || saved <= lastAutoFor.current) return;
    // the last build has this save (it read the document after it) — or, when it failed, already tried
    // it. Not judged by when the PDF was written: a save that lands while a build compiles (an agent's
    // next edit) is not in that PDF, and must be built again.
    if (pdf.sourceAt) { if (saved <= pdf.sourceAt) return; }
    else {
      if (pdf.pdfAt && saved <= pdf.pdfAt + 1500) return;               // (a build recorded before source_at was kept)
      if (pdf.ok === false && pdf.builtAt && saved <= pdf.builtAt) return;
    }
    const t = setTimeout(() => { lastAutoFor.current = saved; void build({ open: false, auto: true }); }, Math.max(0, prefs.autoBuildDelay) * 1000);
    return () => clearTimeout(t);
  }, [docId, save.savedAt, pdf.known, pdf.busy, pdf.pdfAt, pdf.builtAt, pdf.ok, prefs.autoBuild, prefs.autoBuildDelay, pdfPaneVisible, pagesOn]);
  // The PDF opened beside the document (Ctrl+R, the PDF button) must not squeeze the writing into a
  // strip on a laptop: while the document pane is narrower than READABLE_DOC the side panels fold
  // away, the right one first, and come back when the PDF is hidden again. Only right after the PDF
  // appears — a panel opened again by hand afterwards stays open.
  const pdfFold = useRef<{ prev: boolean; active: boolean; files: boolean; right: RightTab | null }>({ prev: false, active: false, files: false, right: null });
  useLayoutEffect(() => {
    const f = pdfFold.current;
    const both = !!pdfPaneVisible && shownPanes.includes('doc');
    if (both !== f.prev) {
      f.prev = both; f.active = both;
      if (!both) {
        if (f.files) setShowFiles(true);
        if (f.right) { const r = f.right; setRightTab(t => t ?? r); }
        f.files = false; f.right = null;
      }
    }
    if (f.files && showFiles) f.files = false;   // opened again by hand: the user's choice from now on
    if (f.right && rightTab) f.right = null;
    if (!f.active) return;
    const w = document.querySelector<HTMLElement>('.editor-column.panes > [data-pane="doc"]')?.getBoundingClientRect().width;
    if (w === undefined || w >= READABLE_DOC) { f.active = false; return; }
    if (rightTab) { f.right = rightTab; setRightTab(null); }
    else if (showFiles) { f.files = true; setShowFiles(false); }
    else f.active = false;
  });
  const cancelBuild = async () => {
    if (!docId) return;
    lastAutoFor.current = Math.max(lastAutoFor.current, save.savedAt);   // cancelled: no automatic build for this save either
    await api.cancelBuild(docId).catch(() => {});
    pollBuild(docId, true);
  };
  /** SyncTeX: the place in the PDF to show (forward search) */
  const [syncTarget, setSyncTarget] = useState<PdfTarget | null>(null);
  /** an article's Sync: its page pane shows the cursor's paragraph (app/ArticlePage.tsx) */
  const [pageSync, setPageSync] = useState(0);
  /** the LaTeX the last build compiled (fetched once, kept with the build state) */
  const builtTex = async (): Promise<string | null> => {
    if (!docId) return null;
    if (pdf.tex) return pdf.tex;
    try { const r = await api.build(docId, true); const t = r.build?.tex ?? null; if (t) setPdf(p => ({ ...p, tex: t })); return t; } catch { return null; }
  };
  /** Forward search: the cursor's line in the built LaTeX (sourcelocate) → its box in the PDF (synctex view) → scroll + flash there. */
  const syncToPdf = async () => {
    if (!docId || !view || !isLyxDoc) return;
    if (isArticle) { showPane('pdf'); setPageSync(Date.now()); return; }
    const tex = await builtTex();
    if (!tex) { notify('SyncTeX needs a built PDF — build it first (Ctrl+R)', 'error'); return; }
    const where = cursorLine(view, tex, mathField, null);
    if (!where) { notify('Could not find the cursor\'s place in the LaTeX source', 'error'); return; }
    try {
      const { boxes } = await api.synctexView(docId, where.line + 1);
      if (!boxes.length) { notify(`SyncTeX has no position for line ${where.line + 1} of the built LaTeX`, 'error'); return; }
      const b = boxes[0];
      showPane('pdf');
      setSyncTarget({ page: b.page, x: b.h, y: b.v - b.H, w: b.W, h: b.H, seq: Date.now() });
    } catch (e) { notify('SyncTeX: ' + (e as Error).message, 'error'); }
  };
  /** Inverse search (a double-click in the PDF): synctex edit → the source line → the paragraph / formula with those words, cursor there. */
  const syncFromPdf = async (page: number, x: number, y: number) => {
    if (!docId || !view) return;
    try {
      const r = await api.synctexEdit(docId, page, x, y);
      if (!r.line) { notify('SyncTeX: nothing is known about this place in the PDF', 'error'); return; }
      const tex = await builtTex();
      if (!tex) return;
      const blocks = docBlocks(view);
      const hit = locateSourceLine(tex, r.line - 1, blocks);
      if (!hit) { notify(`SyncTeX: line ${r.line} of the LaTeX source was not found in the document`, 'error'); return; }
      const b = blocks[hit.index];
      const pos = Math.min(b.kind === 'math' ? b.pos : blockPos(b, hit.offset), view.state.doc.content.size);
      view.dispatch(view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(pos))).scrollIntoView());
      view.focus();
    } catch (e) { notify('SyncTeX: ' + (e as Error).message, 'error'); }
  };
  // the Pages view: on in the document's editor, and the pages of each new PDF placed in it
  useEffect(() => { if (masterView) setPagesView(masterView, pagesOn && isLyxDoc); }, [masterView, pagesOn, isLyxDoc]);
  useEffect(() => {
    const v = masterView;
    if (!v || !docId || !pagesOn || !isLyxDoc || !pdf.pdfAt) return;
    let gone = false;
    void (async () => {
      try {
        const [{ breaks }, tex] = await Promise.all([api.synctexPages(docId), builtTex()]);
        if (!gone && editorRef.current?.view === v) { setPageBreaks(v, breaks, tex); rerender(); }   // (the status bar's page of the cursor)
      } catch { /* the pages stay as they were */ }
    })();
    return () => { gone = true; };
  }, [masterView, docId, pagesOn, isLyxDoc, pdf.pdfAt]);
  // margins and line spacing dragged on the sheets (Ctrl held): into the document's settings — a .tex
  // document's geometry and \spacing (LyX's header keys, written into the managed block), a markdown
  // document's front matter (geometry, linestretch) — then the PDF that shows them
  const headerRef = useRef(headerLines);
  headerRef.current = headerLines;
  const buildRef = useRef(build);
  buildRef.current = build;
  useEffect(() => {
    if (!docId || !isLyxDoc) { setPagesHost(null); return; }
    const id = docId, md = isMarkdownDoc(docId);
    const cm = (pt: number) => `${(pt / (72.27 / 2.54)).toFixed(1)}cm`;
    setPagesHost({
      stretch: () => spacingStretch(headerRef.current.find(l => l.startsWith('\\spacing '))?.slice(9)),
      save: async c => {
        try {
          if (md) {
            const set: Record<string, unknown> = {};
            if (c.stretch !== undefined) set.linestretch = Math.abs(c.stretch - 1) > 0.004 ? c.stretch : null;
            if (c.margins) {
              // the margins replaced, the geometry's other options (paper, head height …) kept, in the form the file had
              const g = (await api.mdFrontMatter(id)).vars.geometry;
              const list = Array.isArray(g) ? g.map(String) : typeof g === 'string' && g.trim() ? g.split(/\s*,\s*/) : [];
              const rest = list.filter(o => !/^\s*(margin|[lrtbhv]?margin|left|right|top|bottom|inner|outer)\s*=/.test(o));
              const m = [`top=${cm(c.margins.top)}`, `bottom=${cm(c.margins.bottom)}`, `left=${cm(c.margins.left)}`, `right=${cm(c.margins.right)}`];
              set.geometry = Array.isArray(g) ? [...rest, ...m] : [...rest, ...m].join(', ');
            }
            await api.setMdFrontMatter(id, set);
          } else {
            const set: Record<string, string> = {};
            if (c.stretch !== undefined) set.spacing = spacingValue(c.stretch);
            if (c.margins) Object.assign(set, { use_geometry: 'true', leftmargin: cm(c.margins.left), rightmargin: cm(c.margins.right), topmargin: cm(c.margins.top), bottommargin: cm(c.margins.bottom) });
            await api.setHeader(id, { set });
          }
          // (not as an automatic build: one running already, from the last drag, builds once more after)
          void buildRef.current({ open: false });
          return true;
        } catch (e) { notify('Could not change the page setup: ' + (e as Error).message, 'error'); return false; }
      },
    });
    return () => setPagesHost(null);
  }, [docId, isLyxDoc]);
  const togglePages = () => {
    const next = !pagesOn;
    setPagesFor({ doc: docId, on: next });
    try { if (next) localStorage.setItem(pagesKeyOf(docId), '1'); else localStorage.removeItem(pagesKeyOf(docId)); } catch { /* private window: for this visit */ }
    if (next && docId && isLyxDoc && !pdf.pdfAt && !pdf.busy) { notify('Building the PDF to see where its pages break…'); void build({ open: false, auto: true }); }
  };
  const showTex = async () => {
    if (!docId) return;
    if (pdf.tex) { setDialog({ name: 'tex', arg: pdf.tex }); return; }
    try { const r = await api.build(docId, true); if (r.build?.tex) { setPdf(p => ({ ...p, tex: r.build!.tex })); setDialog({ name: 'tex', arg: r.build.tex }); return; } } catch { /* fall through */ }
    const r = await api.export(docId, 'tex');
    setDialog({ name: 'tex', arg: r.tex ?? '' });
  };

  const toggleTracking = () => setTrackingTo(!editorContext.trackChanges);
  /** Suggesting on or off: the document's setting (Applies to ▸ Everyone) or this person's own (Only me) */
  const setTrackingTo = async (next: boolean) => {
    if (!docId) return;
    const forAll = modeScope() === 'everyone';
    const set = forAll ? { tracking_changes: String(next) } : undefined;
    if (next && editorContext.changeAuthorId === undefined) {
      // register this user as an author in the document header
      const id = hashAuthor(user.name);
      const lines = [...headerLines];
      const idx = lines.findIndex(l => l.startsWith('\\author '));
      const line = `\\author ${id} "${user.name}" ""`;
      if (idx >= 0) lines.splice(idx, 0, line); else lines.push(line);
      await api.setHeader(docId, { headerLines: lines, set });
      editorContext.changeAuthorId = id;
      // the editor reads the authors from editorContext.meta (the status bar's "Inserted by …", the change menu)
      const m = editorContext.meta;
      if (m && !m.authors.some(a => a.id === id)) { const next = { ...m, authors: [...m.authors, { id, name: user.name }] }; editorContext.meta = next; setMeta(next); }
    } else if (forAll) {
      await api.setHeader(docId, { set });
    }
    setOwnSuggesting(docId, forAll ? null : next);
    if (forAll) setDocTracking(next);
    editorContext.trackChanges = next;
    setTracking(next);
    notify(next ? `Change tracking ON (as ${user.name}${forAll ? '' : ', for you alone'})` : `Change tracking OFF${forAll ? '' : ' for you'}`);
  };
  /** the mode switch's "Applies to": to Everyone, this person's own mode becomes the document's */
  const setTrackingScope = (to: ModeScope) => {
    if (!docId) return;
    const push = switchModeScope(docId, to, docTracking);
    if (push !== null) void setTrackingTo(push);
    else notify(to === 'me' ? 'Editing or suggesting now applies to you alone' : 'Editing or suggesting now applies to everyone editing the document');
  };

  const [repairing, setRepairing] = useState(false);
  const runRepair = async () => {
    if (!docId) return;
    setRepairing(true);
    try {
      const r = await api.repair(docId);
      const m = await api.meta(docId);
      setMeta(m); editorContext.meta = m;
      if (r.fixed.length) notify(`Repaired: ${r.fixed.join(', ')}${r.remaining.length ? ` — ${r.remaining.length} issue(s) still need attention` : ''}`);
      else notify(r.remaining.length ? 'Nothing here can be fixed automatically — try Escalate to AI' : 'No issues found');
    } catch (e) { notify(String(e), 'error'); }
    finally { setRepairing(false); }
  };

  const toggleMargin = () => {
    const next = !marginMode;
    setMarginModeState(next);
    localStorage.setItem('ol.margin', next ? '1' : '0');
    if (masterView) setMarginMode(masterView, next);
    for (const h of childRefs.current.values()) setMarginMode(h.view, next);
  };

  const labels = useMemo(() => {
    if (!view) return [] as { name: string; context: string; file?: string }[];
    const out: { name: string; context: string; file?: string }[] = [];
    const scan = (v: EditorView, file?: string) => v.state.doc.descendants((node, pos) => {
      if (node.type.name === 'command' && node.attrs.cmd === 'label') {
        const $p = v.state.doc.resolve(pos);
        out.push({ name: unquote(commandParams(node).get('name')), context: $p.parent.textContent.slice(0, 60), file });
      } else if (node.type.name === 'math_display') {
        for (const m of String(node.attrs.latex).matchAll(/\\label\{([^}]*)\}/g)) out.push({ name: m[1], context: '(equation)', file });
      }
      return true;
    });
    scan(view);
    if (masterView && masterView !== view) scan(masterView, docPathOf(viewDocId(masterView)));
    for (const h of childRefs.current.values()) if (h.view !== view) scan(h.view, docPathOf(viewDocId(h.view)));
    // labels from the master document and its other children (server-side scan)
    const own = new Set(out.map(l => l.name));
    for (const l of meta?.labels ?? []) if (!own.has(l.name) && l.file !== meta?.path) out.push({ name: l.name, context: l.context, file: l.file });
    return out;
  }, [dialog, view, meta]);

  const labelNames = () => labels.map(l => l.name);
  const refCountOf = (nm: string): number => {
    if (!view || !nm) return 0;
    let n = 0;
    view.state.doc.descendants(node => {
      if (node.type.name === 'command' && node.attrs.cmd === 'ref') {
        const target = unquote(commandParams(node).get('reference'));
        if (target.split(',').map(t => t.trim()).includes(nm)) n++;
      }
      return true;
    });
    return n;
  };

  const layouts = meta?.layouts?.length ? meta.layouts : STANDARD_LAYOUTS;
  // the Share button (top right) belongs to the project's owner; the panel reports the project it shows
  const shareProject = curProject && curProject.role === 'owner' && curProject.via !== 'admin' && (!docId || projectOfDoc(docId.replace(/^(text|pdf):/, '')) === curProject.name) ? curProject.name : null;
  const base = (id: string) => id.split('/').pop() ?? id;
  // the project's name: the tab already names the file (a combined master shows the children it includes)
  const docLabel = docId ? (combined && childIds.length ? [docId, ...childIds].map(base).join(' + ') : projectShortName(projectOfDoc(docId.replace(/^(text|pdf):/, '')))) : '';

  // Help is available everywhere (the start screen has no other menus; feedback must be reachable there)
  const helpMenu: MenuDef = { title: 'Help', search: true, items: [
    { label: PALETTE_LABEL, shortcut: PALETTE_DEFAULT, action: openPalette },
    { label: 'Take the tour', action: () => openTour('intro') },
    { label: 'Keyboard shortcuts', action: () => setDialog({ name: 'help' }) },
    { sep: true },
    { label: 'Report a problem / send feedback…', action: () => setDialog({ name: 'feedback' }) },
    { label: 'Privacy notice', action: () => void window.open('/privacy', '_blank', 'noopener') },
    { label: 'Terms of use', action: () => void window.open('/terms', '_blank', 'noopener') },
    { label: 'OverLyX for VS Code (.vsix download)', action: () => { const a = document.createElement('a'); a.href = '/api/vscode-extension'; a.download = ''; document.body.appendChild(a); a.click(); a.remove(); notify('Downloading the extension — install it in VS Code with “Extensions: Install from VSIX…”'); } },
    { label: 'About OverLyX', action: () => void uiAlert('About OverLyX', 'OverLyX — a collaborative WYSIWYM editor for LaTeX documents (LyX’s “what you see is what you mean”).\nDocuments are ordinary .tex files (change tracking and comments live in the file as macros and comment blocks); formulas are edited with a port of LyX\'s math editor; collaboration via Yjs CRDTs.') },
  ] };
  /** File ▸ New slides / poster / page…, wherever a project is open (a document, a text file, the project's page) */
  const newSlidesItem = { label: 'New slides / poster / page…', action: () => { const p = docId ? projectOfDoc(textId ?? docId) : null; if (p) setNewLayoutFor(p); } };
  const importLyxItem = { label: 'Import from LyX…', disabled: curProject?.role === 'view', action: () => {
    const p = docId ? projectOfDoc(textId ?? docId) : null;
    if (p) chooseLyxImport(p, '', r => { setRefreshKey(k => k + 1); location.hash = '#/' + r.id; });
  } };
  const projectRootMenus: MenuDef[] = docId ? [
    { title: 'File', items: [
      { label: 'New…', shortcut: 'Ctrl+N', action: () => editorContext.ui?.newFile() },
      newSlidesItem,
      importLyxItem,
      { label: 'Download LyX project (.zip)', action: () => window.open(`/api/projects/${encodeURIComponent(projectOfDoc(docId))}/zip?format=lyx`) },
      { label: 'Share project…', action: () => setShareFor(projectOfDoc(docId)) },
      { label: 'Git repository…', action: () => setGitFor(projectOfDoc(docId)) },
      { sep: true },
      { label: 'Close (back to the projects)', action: closeDoc },
    ] },
  ] : [];
  const textFileMenus: MenuDef[] = docId ? [
    { title: 'File', items: [
      { label: 'New…', shortcut: 'Ctrl+N', action: () => editorContext.ui?.newFile() },
      newSlidesItem,
      importLyxItem,
      { label: 'Open… (files panel)', shortcut: 'Ctrl+O', action: () => setShowFiles(true) },
      ...(isPdfTab ? [] : [{ label: 'Saved automatically (Ctrl+S saves now)', disabled: true, action: () => {} }]),
      { sep: true },
      { label: 'Download', action: () => window.open(`/api/projects/${encodeURIComponent(projectOfDoc(textId!))}/file/${docPathOf(textId!).split('/').map(encodeURIComponent).join('/')}`) },
      { label: 'Share project…', action: () => setShareFor(projectOfDoc(textId!)) },
      { label: 'Git repository…', action: () => setGitFor(projectOfDoc(textId!)) },
      { sep: true },
      { label: 'Close (back to the projects)', action: closeDoc },
    ] },
  ] : [];
  const textColor = markValue(view, 'color');
  // a slide deck's own menus (deliverable/DeliverableEditor.tsx: Edit, View, Insert, Slide, Arrange)
  const editorMenus = useEditorMenus();
  // the shortcut table is searchable too (a match opens the table)
  const helpSearchEntries = useMemo(() => HELP_ROWS.map(([k, v]) => ({ id: 'Keyboard shortcuts ▸ ' + v, label: v, path: ['Keyboard shortcuts'], shortcut: k, fixed: true, action: () => setDialog({ name: 'help' }) })), []);
  const sectionItems = useMemo<MenuDef['items']>(() => {
    const v = masterView;
    if (!v) return [];
    return outline.filter(it => it.level < 99 && it.layout !== 'Title').slice(0, 120).map(it => ({
      label: `${'\u2003'.repeat(Math.max(0, it.level - 1))}${it.num ? it.num + '\u2002' : ''}${it.text}`,
      action: () => jumpToOutline(v, it),
      stat: '<heading>',   // the label is the heading's text: the usage statistics see only that a heading was chosen
    }));
  }, [outline, masterView]);
  const toggleCellLine = (key: string) => {
    if (!view) return;
    const $from = view.state.selection.$from;
    let cur: any = null;
    for (let d = $from.depth; d > 0; d--) if ($from.node(d).type.name === 'table_cell') { cur = $from.node(d); break; }
    const attrs: [string, string][] = cur ? JSON.parse(cur.attrs.attrs || '[]') : [];
    const on = attrs.find(a => a[0] === key)?.[1] === 'true';
    run(C.setCellAttr(key, on ? null : 'true'));
  };

  const editingMenus = documentMenus({ view, meta, run, runView, setDialog, textColor, tracking, changeInfo, toggleTracking, toggleCellLine, setFindOpen,
    healthItems: [
      { label: 'Document health ▸', sub: [
        { label: meta?.health.length ? `${meta.health.length} issue(s) found` : 'No issues found', disabled: true },
        { label: 'Repair', disabled: !meta?.health.some(h => h.fixable), action: runRepair },
        { label: 'Escalate to AI…', disabled: !meta?.health.length, action: () => setDialog({ name: 'airepair' }) },
      ] },
      { sep: true },
    ],
    reloadMetadata: () => { if (docId) api.meta(docId).then(m => { setMeta(m); editorContext.meta = m; if (masterView) refreshMacros(masterView, m.macros); notify('Metadata reloaded'); }); },
  });
  // back from Google's consent screen (File ▸ Google Docs… ▸ Connect): the dialog again, with what happened
  useEffect(() => {
    const m = /[?&]gdocs=([^&]*)/.exec(location.hash);
    if (!m || !docId) return;
    const what = decodeURIComponent(m[1]);
    history.replaceState(null, '', location.pathname + location.search + location.hash.replace(/[?&]gdocs=[^&]*/, ''));
    if (what !== 'connected') notify(what, 'error');
    setDialog({ name: 'gdocs' });
  }, [docId]);
  // back from LessWrong's consent page (File ▸ LessWrong… ▸ Connect): the dialog again
  useEffect(() => {
    const m = /[?&]lesswrong=([^&]*)/.exec(location.hash);
    if (!m || !docId) return;
    const what = decodeURIComponent(m[1]);
    history.replaceState(null, '', location.pathname + location.search + location.hash.replace(/[?&]lesswrong=[^&]*/, ''));
    if (what !== 'connected') notify(what, 'error');
    setDialog({ name: 'lesswrong' });
  }, [docId]);
  const menus: MenuDef[] = [...(isProjectRoot ? projectRootMenus : docId && !isLyxDoc ? [...textFileMenus, ...(isHtmlTab ? editorMenus : [])] : docId ? [
    { title: 'File', items: [
      { label: 'New…', shortcut: 'Ctrl+N', action: () => editorContext.ui?.newFile() },
      newSlidesItem,
      importLyxItem,
      { label: 'New whiteboard…', action: () => {
        const p = textId ? projectOfDoc(textId) : null;
        if (!p) return;
        void uiPrompt('New Whiteboard', 'New whiteboard name:', 'whiteboard.board').then(name0 => {
        let name = name0;
        if (!name) return;
        if (!name.endsWith('.board')) name += '.board';
        api.upload(p, name, new Blob(['{"overlyx":"board","v":1,"objects":{\n}}\n'], { type: 'application/octet-stream' }), { overwrite: false })
          .then(() => { location.hash = '#/' + p + '/' + name; setRefreshKey(k => k + 1); })
          .catch(e => notify('Could not create the whiteboard: ' + (e as Error).message, 'error'));
        });
      } },
      { label: 'Open… (files panel)', shortcut: 'Ctrl+O', action: () => setShowFiles(true) },
      { label: save.state === 'offline' ? 'Offline — changes are saved on this device' : save.state === 'saving' ? 'Saving…' : 'All changes saved automatically', disabled: true, action: () => {} },
      { sep: true },
      { label: 'Export ▸', sub: [
        { label: 'PDF (latexmk)', shortcut: 'Ctrl+R', action: () => build() },
        { label: 'LaTeX source (as built)…', action: async () => { const r = await api.export(docId, 'tex'); setDialog({ name: 'tex', arg: r.tex ?? '' }); } },
        { label: 'Download .tex', action: () => window.open(`/api/docs/${encodeURIComponent(docId)}/tex?download=1`) },
        { label: 'Download LyX (.lyx)', action: () => window.open(`/api/docs/${encodeURIComponent(docId)}/lyx`) },
        { label: 'LyX project with figures (.zip)', action: () => window.open(`/api/projects/${encodeURIComponent(projectOfDoc(docId))}/zip?format=lyx`) },
        { label: 'Download PDF', action: () => window.open(`/api/docs/${encodeURIComponent(docId)}/pdf?download=1`) },
      ] },
      { label: 'Versions…', action: () => setRightTab('versions') },
      { label: 'Share project…', action: () => setShareFor(projectOfDoc(docId)) },
      { label: 'Google Docs (sync, comments)…', action: () => setDialog({ name: 'gdocs' }) },
      { label: 'LessWrong (sync, comments)…', action: () => setDialog({ name: 'lesswrong' }) },
      { label: 'Git repository…', action: () => setGitFor(projectOfDoc(docId)) },
      { sep: true },
      { label: 'Close (back to the projects)', action: closeDoc },
    ] },
    editingMenus.edit,
    editorViewMenu({ combined, setCombined, marginMode, toggleMargin, run, showRuler, setShowRuler, tbMode, setToolbar, textWidth, setTextWidth, stepTextWidth,
      hostItems: [
      { label: 'LaTeX source beside the document (raw view)', shortcut: 'Ctrl+Alt+S', checked: panes.shown.tex, action: toggleRawSplit },
      { label: 'Outline', shortcut: 'Ctrl+Alt+O', checked: showFiles, action: () => setShowFiles(!showFiles) },
      { label: 'PDF preview', checked: panes.shown.pdf, action: () => flipPane('pdf') },
      // (Google Docs' print layout, with TeX's page breaks: editor/plugins/pages.ts)
      { label: 'Pages (breaks as in the PDF)', checked: pagesOn, action: togglePages },
      { label: 'WYSIWYM document', checked: panes.shown.doc, action: () => flipPane('doc') },
      { label: 'Versions', checked: rightTab === 'versions', action: () => setRightTab(rightTab === 'versions' ? null : 'versions') },
      { sep: true },
      ],
      themeItems: [
      { label: 'Interface ▸', sub: [
        { label: 'Classic — LyX’s toolbars and menus', checked: ui === 'classic', action: () => setUiMode('classic') },
        { label: 'Modern — one calm toolbar, tools appear as needed', checked: ui === 'modern', action: () => setUiMode('modern') },
      ] },
      { label: 'Theme ▸', sub: [
        { label: 'Default (follows the system)', checked: themePref === 'system', action: () => setThemePref('system') },
        { label: 'Light', checked: themePref === 'light', action: () => setThemePref('light') },
        { label: 'Dark', checked: themePref === 'dark', action: () => setThemePref('dark') },
      ] },
      ],
      hostToolbars: [
        { label: 'Version Control', checked: tbMode('vcs') === 'on', action: () => setToolbar('vcs', tbMode('vcs') === 'on' ? 'off' : 'on') },
      ],
    }),
    editingMenus.insert,
    { title: 'Navigate', items: [
      { label: 'Outline pane (files panel)', shortcut: 'Ctrl+Alt+O', action: () => setShowFiles(true) },
      { label: 'Go to label…', action: () => { void uiPrompt('Go to Label', 'Label:').then(n => { if (n) gotoLabel(n, view ?? undefined); }); } },
      { label: isArticle ? 'Show in the page' : 'Sync to PDF (forward search)', shortcut: 'Ctrl+Alt+J', action: () => { void syncToPdf(); } },
      { sep: true },
      { label: 'Back', shortcut: NAV_BACK_KEY, disabled: !navHistory.canBack(), action: navBack },
      { label: 'Forward', shortcut: NAV_FORWARD_KEY, disabled: !navHistory.canForward(), action: navForward },
      { label: 'Next document of the project', action: () => { const d = curProject ? projectDocs(curProject, true).map(x => `${curProject.name}/${x}`) : []; const i = d.indexOf(docId); const n = d[(i + 1) % d.length]; if (n && n !== docId) openInTab(n); } },
      { label: 'Beginning of document', shortcut: 'Ctrl+Home', action: () => { if (view) { view.dispatch(view.state.tr.setSelection(TextSelection.atStart(view.state.doc)).scrollIntoView()); view.focus(); } } },
      { label: 'End of document', shortcut: 'Ctrl+End', action: () => { if (view) { view.dispatch(view.state.tr.setSelection(TextSelection.atEnd(view.state.doc)).scrollIntoView()); view.focus(); } } },
      // the document's sections (LyX's Navigate menu lists them too): a click jumps, the palette finds them
      ...(sectionItems.length ? [{ sep: true } as const, ...sectionItems] : []),
    ] },
    editingMenus.document,
    { title: 'Tools', items: [
      { label: 'Spell checking', checked: prefs.spellcheck, action: () => setPref('spellcheck', !prefs.spellcheck) },
      { label: 'Shortcut tips after mouse actions', checked: prefs.shortcutTips, action: () => setPref('shortcutTips', !prefs.shortcutTips) },
      { sep: true },
      { label: 'AI assistance ▸', sub: [
        { label: ai === null ? 'Checking the server…' : ai.available ? `Models: ${(prefs.aiModel && inlineModelLabel(prefs.aiModel)) || prefs.aiModel || ai.model} (${REWRITE_KEY}) · ${prefs.aiCompletionModel || ai.completionModel} (autocomplete)` : 'Not configured on this server (OPENROUTER_API_KEY)', disabled: true },
        { label: 'Choose the models… (Settings)', action: () => setDialog({ name: 'preferences', arg: 'ai' }) },
        { label: 'Show the ✦ button on the toolbar (autocomplete on/off)', checked: prefs.aiButton, action: () => setPref('aiButton', !prefs.aiButton) },
        { sep: true },
        { label: `Rewrite with AI (${REWRITE_KEY})`, checked: prefs.aiRewrite, action: () => setPref('aiRewrite', !prefs.aiRewrite) },
        { label: 'Autocomplete text (ghost text, Tab inserts)', checked: prefs.aiCompleteText, action: () => setPref('aiCompleteText', !prefs.aiCompleteText) },
        { label: 'Autocomplete formulas', checked: prefs.aiCompleteMath, action: () => setPref('aiCompleteMath', !prefs.aiCompleteMath) },
        { sep: true },
        { label: 'Rewrite selection with AI…', shortcut: REWRITE_KEY, disabled: !prefs.aiRewrite, action: () => runView(v => openRewrite(v)) },
      ] },
      { sep: true },
      { label: 'Settings…', action: () => setDialog({ name: 'preferences' }) },
    ] },
  ] : []), helpMenu];

  /** A paper was added to cited.bib: make the entry known, list cited.bib in the BibTeX inset, refresh the metadata. */
  const onBibAdded = (r: BibAddResult) => {
    rememberBib([r.entry]);
    if (r.existed) return;
    const where = masterView ?? view;
    const bib = r.bib ?? r.file;
    const st = where ? C.ensureBibFile(where, bib) : 'none';
    if (st === 'added') notify(`Added ${bib} to the document's bibliography`);
    else if (st === 'none') notify(`${r.file} has the entry — add a BibTeX bibliography with "${bib.replace(/\.bib$/, '')}" (Insert ▸ BibTeX bibliography…) so that it is printed`, 'error');
    if (docId) api.meta(docId).then(m => { setMeta(m); editorContext.meta = m; }).catch(() => {});
  };
  /** Entries picked in the citation dialog become known to the editor (for rendering author/year) even if they were not cited before. */
  /** a citation in a document without a bibliography prints as [?] — say so (once per document), and how to add one */
  const bibHinted = useRef(new Set<string>());
  const noBibliographyHint = () => {
    const v = activeViewRef.current ?? editorRef.current?.view;
    if (!v || !docId || meta?.master || bibHinted.current.has(docId)) return;
    let has = false;
    v.state.doc.descendants(n => { if (has) return false; if (n.type.name === 'command' && String(n.attrs.params ?? '').includes('LatexCommand bibtex')) has = true; return !has; });
    if (has) return;
    bibHinted.current.add(docId);
    notify('This document has no bibliography yet, so its citations print as [?] in the PDF — add one with Insert ▸ BibTeX bibliography… (usually at the end).');
  };
  const rememberBib = (entries: { key: string; author: string; year: string; title: string }[]) => {
    const m = editorContext.meta;
    if (!m || !entries.length) return;
    for (const e of entries) if (!m.bib.some(b => b.key === e.key)) m.bib.push(e);
    setMeta({ ...m });
  };


  /* ------------------------------------------------------------------ toolbars (LyX stdtoolbars.inc) */
  const mathExec = mathExecutor(() => activeViewRef.current ?? editorRef.current?.view);
  const mathPanels = useMathPanels(mathExec);
  const clipboard = toolbarClipboard(() => activeViewRef.current ?? editorRef.current?.view, notify);
  const aiComplete = prefs.aiCompleteText || prefs.aiCompleteMath;
  const docStats = useMemo(() => (view ? documentStats(view) : null), [view, docTick, selTick]);
  // The LyX toolbars (toolbars.tsx — one definition with the VS Code extension); what only the web
  // client has (files, navigation history, the outline sidebar, ink, AI, the master's PDF) goes into the slots.
  const tb = buildToolbars({
    view, docId, meta, headerLines, prefs, layout, mathField, tracking, viewing, marginMode, tbMode, setToolbar,
    run, runView, mathExec, mathPanels, clipboard, setDialog, openFind: () => setFindOpen(true), notify,
    toggleTracking: () => { void toggleTracking(); }, toggleMargin,
    build: () => { void build(); }, updatePdf: () => { void build({ open: false }); }, syncToPdf: () => { void syncToPdf(); },
    slots: {
      leading: [
        { id: 'new', title: 'New file — a document, a whiteboard, a .bib … (Ctrl+N)', icon: 'new', action: () => editorContext.ui?.newFile() },
        { id: 'open', title: 'Open (Ctrl+O)', icon: 'open', action: () => setShowFiles(true) },
      ],
      navigation: [{ id: 'navback', title: 'Navigate back (Ctrl+Alt+←)', icon: 'navback', action: navBack }],
      sidebars: [{ id: 'outline', title: LEFT_TITLE, icon: 'outline', action: () => setShowFiles(s => !s), active: showFiles }],
      tools: [
        { id: 'ink', title: inkMode && getInk().folded ? 'Margin drawing is on — click for the pens, colours and widths again' : inkMode ? 'Margin drawing is on — click to put the pen away' : 'Draw in the margins — pen, highlighter, eraser, lasso (pans sideways for more space); a pen draws on a margin right away', icon: 'ink',
          action: () => { if (inkMode && getInk().folded) setInk({ folded: false }); else setInkMode(m => !m); }, active: inkMode },
        // the ✦ button exists only once it is enabled in the preferences; it switches autocomplete (text + formulas) on and off
        ...(prefs.aiButton ? [{ id: 'ai', title: aiComplete ? 'AI autocomplete is on — click to switch it off' : 'AI autocomplete is off — click to switch it on (ghost text after a pause while typing; Tab inserts it)', icon: 'ai', action: () => { setPref('aiCompleteText', !aiComplete); setPref('aiCompleteMath', !aiComplete); notify(!aiComplete ? 'AI autocomplete on' : 'AI autocomplete off'); }, active: aiComplete } as ToolButton] : []),
      ],
      pdf: meta?.master ? [{ id: 'pdfmaster', title: `View master document (${meta.master.split('/').pop()})`, icon: 'viewmaster', action: () => openInTab(meta.master!) }] : [],
    },
  });
  // The margin-ink toolbar (bottom-docked while drawing is on): tool, then the colour and width
  // of the pen in use — pen and highlighter each keep their own (Goodnotes), so the swatches and
  // dots change with the tool; picking one while erasing or lassoing takes the pen up again.
  // The swatches and dots are presets: one click selects, a click on the selected one opens a
  // picker that replaces it (Goodnotes). The laser is the one tool that works over the text too.
  const inkGroups = inkToolbar();
  // The LyX Version Control toolbar, mapped onto the project's git repository (off by default, as in LyX).
  const vcsGroups: ToolButton[][] = [
    [
      { id: 'vc-git', title: 'Git repository — clone address, access tokens, mirror', icon: 'vcregister', action: () => { const p = projectOfDoc(docId ?? ''); if (p) setGitFor(p); } },
      { id: 'vc-log', title: 'Revision log (the Versions panel)', icon: 'vclog', action: () => { setRightTab('versions'); setSelVersion(v => v + 1); } },
      { id: 'vc-compare', title: 'Compare with an older revision (the Versions panel)', icon: 'vccompare', action: () => { setRightTab('versions'); setSelVersion(v => v + 1); } },
    ],
  ];

  const insetDialogNode = () => {
    if (!view) return null;
    const pos = typeof dialog?.arg === 'number' ? dialog.arg : undefined;
    if (pos !== undefined) { const n = view.state.doc.nodeAt(pos); return n ? { node: n, pos } : null; }
    return C.nearestNode(view.state, ['inset', 'command', 'graphics', 'leaf', 'table']);
  };

  /** Insert LaTeX into the focused formula, or into a new inline formula at the cursor. */
  const insertInMath = (latex: string) => {
    const active = activeMathField();
    if (active) { active.execute('insert', latex); return; }
    if (view) { C.insertMath(false)(view); setTimeout(() => activeMathField()?.execute('insert', latex), 60); }
  };
  const renderDialog = () => {
    if (!dialog) return null;
    // dialogs that do not need an editor (start screen, text files)
    if (dialog.name === 'feedback') return <FeedbackDialog docId={docId} onClose={() => { setDialog(null); view?.focus(); }} />;
    if (dialog.name === 'help') return <HelpDialog onClose={() => { setDialog(null); view?.focus(); }} />;
    if (dialog.name === 'preferences') return <SettingsPanel ai={ai} user={user!} initial={dialog.arg as 'ai' | 'appearance' | undefined} onClose={() => { setDialog(null); view?.focus(); }} />;
    if (!view || !docId) return null;
    const close = () => { setDialog(null); view.focus(); };
    const project = projectOfDoc(viewDocId(view)) || projectOfDoc(docId);
    const docDir = view.dom.dataset.docDir ?? editorContext.docDir;
    switch (dialog.name) {
      case 'graphics': return <GraphicsDialog meta={meta} project={project} docDir={docDir} onClose={close} onInsert={(f, o) => run(C.insertGraphics(f, o))} />;
      case 'childdoc': return <ChildDocDialog meta={meta} project={project} docDir={docDir} onClose={close} onInsert={(f, k) => { run(C.insertInclude(f, k)); setRefreshKey(k2 => k2 + 1); }} />;
      case 'paragraph': {
        const cur = C.currentParagraph(view.state);
        if (!cur) { setDialog(null); return null; }
        const a = cur.node.attrs;
        return <ParagraphDialog initial={{ align: a.align ?? null, spacing: a.spacing ?? null, noindent: !!a.noindent, labelwidthstring: a.labelwidthstring ?? null }} indentSeparation={!headerLines.some(l => l === '\\paragraph_separation skip')} onClose={close} onApply={p => run(C.setParagraphAttrs({ ...p }))} />;
      }
      case 'tablesettings': {
        const ctx = C.tableContext(view.state);
        if (!ctx) { setDialog(null); notify('The cursor is not in a table'); return null; }
        const m = (json: string) => new Map<string, string>((() => { try { return JSON.parse(json || '[]'); } catch { return []; } })());
        const columns: [string, string][][] = (() => { try { return JSON.parse(ctx.table.attrs.columns || '[]'); } catch { return []; } })();
        // LyX keeps lines on cells: the row tab shows a line as set when every cell of the row has it
        const rowAttrs = m(ctx.row.attrs.attrs);
        for (const k of ['topline', 'bottomline']) { let all = true; ctx.row.forEach(c => { if (m(c.attrs.attrs).get(k) !== 'true') all = false; }); rowAttrs.set(k, all ? 'true' : ''); }
        return <TableSettingsDialog initial={{ cell: m(ctx.cell.attrs.attrs), column: new Map(columns[ctx.colIndex] ?? []), row: rowAttrs, table: m(ctx.table.attrs.features), rowIndex: ctx.rowIndex, colIndex: ctx.colIndex, nrows: ctx.nrows, ncols: ctx.ncols }} onClose={close} onApply={ch => run(C.setTableAttrs(ch))} />;
      }
      case 'delimiters': return <DelimiterDialog onClose={close} onInsert={latex => insertInMath(latex)} />;
      case 'matrix': return <MatrixDialog onClose={close} onInsert={latex => insertInMath(latex)} />;
      case 'table': return <TableDialog onClose={close} onInsert={(r, c) => run(C.insertTable(r, c))} />;
      case 'label': {
        const arg = dialog.arg as { pos?: number; equation?: boolean; initial?: string; hasLabel?: boolean; refCount?: number; onApply?: (n: string) => void; onRemove?: () => void } | undefined;
        if (arg?.equation && arg.onApply) {
          return <LabelDialog initial={arg.initial ?? 'eq:'} editing refCount={arg.refCount ?? 0} existing={labelNames()} onClose={close}
            onInsert={n => arg.onApply!(n)} onRemove={arg.hasLabel ? () => arg.onRemove?.() : undefined} />;
        }
        if (arg?.pos !== undefined) {
          const node = view.state.doc.nodeAt(arg.pos);
          if (node && node.type.name === 'command' && node.attrs.cmd === 'label') {
            const cur = unquote(commandParams(node).get('name'));
            const lpos = arg.pos;
            return <LabelDialog initial={cur} editing refCount={refCountOf(cur)} existing={labelNames()} onClose={close}
              onInsert={n => { if (n !== cur) { C.setLabelName(view, lpos, n); C.renameLabelRefs(view, cur, n); } }}
              onRemove={() => C.deleteLabelAt(view, lpos)} />;
          }
        }
        return <LabelDialog initial={suggestLabel(view)} existing={labelNames()} onClose={close} onInsert={n => run(C.insertLabel(n))} />;
      }
      case 'ref': {
        const target = dialog.arg as { pos?: number; node?: any; prefill?: string } | undefined;
        if (target?.node && target.pos !== undefined) {
          const p = commandParams(target.node);
          const tpos = target.pos, tnode = target.node;
          return <RefDialog view={view} labels={labels} useRefstyle={!!meta?.useRefstyle} initial={{ name: unquote(p.get('reference')), kind: unquote(p.get('package')) === 'cleveref' ? 'cref' : p.get('LatexCommand') ?? 'ref', tuple: unquote(p.get('tuple')) === 'range' ? 'range' : 'list', caps: unquote(p.get('caps')) === 'true' }} onClose={close}
            onInsert={(n, k, o) => view.dispatch(referenceTransaction(view.state, n, k, o, tpos))} />;
        }
        return <RefDialog view={view} labels={labels} useRefstyle={!!meta?.useRefstyle} initial={target?.prefill ? { name: target.prefill, kind: 'ref' } : undefined} onClose={close} onInsert={(n, k, o) => view.dispatch(referenceTransaction(view.state, n, k, o))} />;
      }
      case 'cite': {
        const target = dialog.arg as { pos: number; node: any } | undefined;
        if (target?.node) {
          const p = commandParams(target.node);
          return <CiteDialog meta={meta} docId={docId} project={viewOnly ? undefined : project} onAdded={onBibAdded} initial={{ keys: unquote(p.get('key')).split(',').map(k => k.trim()).filter(Boolean), cmd: p.get('LatexCommand') ?? 'cite', before: unquote(p.get('before')), after: unquote(p.get('after')) }} onClose={close}
            onInsert={(keys, cmd, b, a, entries) => { rememberBib(entries); const params = [`LatexCommand ${cmd}`]; if (a) params.push(`after "${a}"`); if (b) params.push(`before "${b}"`); params.push(`key "${keys.join(',')}"`, 'literal "false"', ''); view.dispatch(view.state.tr.setNodeMarkup(target.pos, undefined, { ...target.node.attrs, params: JSON.stringify(params) })); }} />;
        }
        return <CiteDialog meta={meta} docId={docId} project={viewOnly ? undefined : project} onClose={close} onAdded={onBibAdded} onInsert={(keys, cmd, b, a, entries) => { rememberBib(entries); run(C.insertCite(keys, cmd, b, a)); noBibliographyHint(); }} />;
      }
      case 'href': return <HrefDialog onClose={close} onInsert={(t, n) => run(C.insertHref(t, n))} />;
      case 'mdsettings': return <MarkdownSettingsDialog docId={docId} onClose={close} />;
      case 'textstyles': return <TextStylesPanel docId={docId} headerLines={headerLines} view={view} markdown={isMarkdownDoc(docId)} layout={typeof dialog.arg === 'string' ? dialog.arg : undefined} onClose={close} />;
      case 'settings': return <SettingsDialog docId={docId} meta={meta} headerLines={headerLines} tab={typeof dialog.arg === 'string' ? dialog.arg : undefined} onClose={close} onSaved={() => api.meta(docId).then(m => { setMeta(m); editorContext.meta = m; if (masterView) refreshMacros(masterView, m.macros); })} />;
      case 'macros': return <MacrosDialog meta={meta} onClose={close} />;
      case 'airepair': return docId ? <AiRepairDialog docId={docId} onClose={close} onApplied={() => api.meta(docId).then(m => { setMeta(m); editorContext.meta = m; })} /> : null;
      case 'stats': return view ? <StatsDialog view={view} onClose={close} /> : null;
      case 'gdocs': return docId ? <GoogleDocsDialog docId={docId} onClose={close} notify={notify} /> : null;
      case 'lesswrong': return docId ? <LessWrongDialog docId={docId} onClose={close} notify={notify} /> : null;
      case 'tex': return <TexDialog tex={String(dialog.arg ?? '')} onClose={close} />;
      case 'layout': return <LayoutPicker layouts={layouts} onClose={close} onPick={n => run(C.setLayout(n))} />;
      case 'argument': { run(C.insertArgument(String(dialog.arg ?? '1'))); setDialog(null); return null; }
      case 'inset': {
        const target = insetDialogNode();
        if (!target) { setDialog(null); notify('No inset at the cursor'); return null; }
        if (target.node.type.name === 'graphics') {
          const params: string[] = (() => { try { return JSON.parse(target.node.attrs.params || '[]'); } catch { return []; } })();
          return <GraphicsDialog meta={meta} project={project} docDir={docDir} initial={C.graphicsOpts(params)} onClose={close}
            onInsert={(f, o) => view.dispatch(view.state.tr.setNodeMarkup(target.pos, undefined, { ...target.node.attrs, params: JSON.stringify(C.graphicsParams(f, o)) }))} />;
        }
        if (target.node.type.name === 'command' && target.node.attrs.cmd === 'href') {
          const p = commandParams(target.node);
          return <HrefDialog initial={{ target: unquote(p.get('target')), name: unquote(p.get('name')) }} onClose={close} onInsert={(t, n) => view.dispatch(view.state.tr.setNodeMarkup(target.pos, undefined, { ...target.node.attrs, params: JSON.stringify(['LatexCommand href', `name "${n}"`, `target "${t}"`, 'literal "false"', '']) }))} />;
        }
        if (target.node.type.name === 'command' && target.node.attrs.cmd === 'label') { setDialog({ name: 'label', arg: { pos: target.pos } }); return null; }
        if (target.node.type.name === 'table') { setDialog({ name: 'tablesettings' }); return null; }
        return <InsetDialog node={target.node} onClose={close} onApply={attrs => view.dispatch(view.state.tr.setNodeMarkup(target.pos, undefined, { ...target.node.attrs, ...attrs }))} />;
      }
    }
    return null;
  };

  /** The tour practises on the user's example project (a document of their own); failing that, on the open document. */
  const openExample = useCallback(async (): Promise<boolean> => {
    try {
      const { projects } = await api.projects();
      const ex = projects.find(p => p.kind === 'example' && p.via === 'owner');
      const doc = ex && projectDocs(ex)[0];
      if (ex && doc) { openInTab(`${ex.name}/${doc}`); return true; }
    } catch { /* offline: fall through */ }
    return isLyxDoc;
  }, [openInTab, isLyxDoc]);
  const endTour = useCallback((how: TourEnd) => { rememberTour(how); setTour(null); }, []);

  const sourceTarget: SourceTarget | null = (() => {
    if (!view) return null;
    if (masterView && view === masterView && editorRef.current) return { view, ydoc: editorRef.current.ydoc, docId: docId! };
    for (const [id, h] of childRefs.current) if (h.view === view) return { view, ydoc: h.ydoc, docId: id };
    return editorRef.current ? { view: editorRef.current.view, ydoc: editorRef.current.ydoc, docId: docId! } : null;
  })();

  const modeRow = tbMode('standard') !== 'off' ? 'standard' : tbMode('viewupdate') !== 'off' || tbMode('extra') !== 'off' ? 'samerow' : 'own';
  const modeSwitch = (
    <EditModeSwitch mode={editModeOf(tracking, viewing)} canEdit={!viewOnly} view={view}
      views={() => [masterView, ...[...childRefs.current.values()].map(h => h.view)].filter((v): v is EditorView => !!v)}
      onMode={m => applyEditMode(m, { tracking, toggleTracking: () => { void toggleTracking(); }, setViewing })}
      scope={{ doc: docTracking, set: setTrackingScope }} />
  );

  // "⇄ In sync with VS Code / Google Docs": the document is kept in step with a copy elsewhere (SyncChip.tsx)
  const externalSync = useExternalSync(textId);
  const syncChip = <SyncChip ext={externalSync} onOpen={what => { if (what !== 'git' && isLyxDoc) setDialog({ name: what }); else if (textId) setGitFor(projectOfDoc(textId)); }} />;

  // a guest signs in: with Google directly (back to this document afterwards), else on the sign-in page
  const signIn = () => { if (google) location.assign(googleSignInUrl()); else onSignIn(); };

  // the toolbar rows (Modern's one row; Classic's Standard, View/Update + Extra, VCS; a layout page's Layout row)
  const toolbarRows = (<>
    {/* the Modern interface: one rounded row (toolbars.tsx `modern`), the mode switch at its end, as in Google Docs */}
    {isLyxDoc && ui === 'modern' && (
      <div class="tb-modern-row">
        <Toolbar id="modern" layouts={layouts} layout={layout} onLayout={n => run(C.setLayout(n))} onEditStyle={n => setDialog({ name: 'textstyles', arg: n })} layoutsAt={2} groups={tb.modern} collapse={MODERN_COLLAPSE} />
        <span class="tb-modern-end">
          <ZoomControl zoom={zoom} onZoom={setZoom} />
          <button type="button" class="tb-pdf-btn" title="Build and show the PDF (Ctrl+R)" onClick={() => { void build(); }}>PDF</button>
          {modeSwitch}
        </span>
      </div>
    )}
    {/* the mode switch (Editing · Suggesting · Viewing) ends the first toolbar row, as in Google Docs */}
    {isLyxDoc && ui !== 'modern' && (modeRow === 'standard' || modeRow === 'own') && (
      <div class="tb-toprow">
        {modeRow === 'standard' && <Toolbar id="standard" layouts={layouts} layout={layout} onLayout={n => run(C.setLayout(n))} onEditStyle={n => setDialog({ name: 'textstyles', arg: n })} groups={tb.standard} />}
        {modeSwitch}
      </div>
    )}
    {/* LyX's default.ui puts View/Update and Extra on one row ("samerow") */}
    {isLyxDoc && ui !== 'modern' && (tbMode('viewupdate') !== 'off' || tbMode('extra') !== 'off') && (
      <div class="tb-samerow">
        {tbMode('viewupdate') !== 'off' && <Toolbar id="viewupdate" groups={tb.viewUpdate} />}
        {tbMode('extra') !== 'off' && <Toolbar id="extra" groups={tb.extra} />}
        {modeRow === 'samerow' && modeSwitch}
      </div>
    )}
    {isLyxDoc && ui !== 'modern' && tbMode('vcs') === 'on' && <Toolbar id="vcs" label="Version Control" groups={vcsGroups} />}
    {isLyxDoc && tb.showLayout && <Toolbar id="layout" label="Layout" groups={tb.layout} />}
  </>);
  return (
    <div class="app">
      <MenuBar menus={menus} user={user} onLogout={onLogout} onSettings={() => setDialog({ name: 'preferences' })} onAppearance={() => setDialog({ name: 'preferences', arg: 'appearance' })} onHome={() => { location.hash = '#/'; }} searchEntries={helpSearchEntries}
        users={isLyxDoc ? status.users : undefined} onJumpToUser={jumpToUser}
        onShare={shareProject ? () => setShareFor(shareProject) : null} shareTitle={shareProject ? `Share “${curProject?.title ?? projectShortName(shareProject)}”: invite people or turn on a link` : undefined}
        onSignIn={user.guest ? signIn : undefined}
        primary={isLyxDoc && <PaneSwitch layout={panes} onChange={changePanes} narrow={narrowPanes} markdown={isMarkdownDoc(docId)} article={isArticle} />}
        right={docId && <span class="doc-title" title={docId}>{docLabel}{meta?.master && !combined && <> · child of <a href={'#/' + meta.master} onClick={e => { e.preventDefault(); openInTab(meta.master!); }}>{meta.master.split('/').pop()}</a></>}</span>}
        title={docId && ui === 'modern' ? (
          <>
            <span class="mt-name" title={docId}>{docPathOf(docId.replace(/^(text|pdf):/, '')).split('/').pop()}</span>
            <span class="mt-project">{curProject?.title ?? projectShortName(projectOfDoc(docId.replace(/^(text|pdf):/, '')))}</span>
            {isLyxDoc && <SaveIndicator save={save} />}
          </>
        ) : undefined} />
      {user.guest && <GuestCallout user={user} project={curProject} google={google} onSignIn={signIn} />}
      {/* the toolbar rows: along the top, or in a column beside the document (app/tbdock.tsx; inside .main below) */}
      <ToolbarDock at="top">{toolbarRows}</ToolbarDock>
      {/* the contextual math / table / review rows are docked at the bottom (before the StatusBar below) */}
      {docId && meta && meta.health.length > 0 && (
        <div class="health-bar">
          <span class="health-icon">⚠</span>
          <span>{meta.health.length === 1 ? '1 structural issue' : `${meta.health.length} structural issues`} found in this file (probably from an external edit): {meta.health.map(h => h.message).join(' ')}</span>
          <span style="flex:1" />
          {meta.health.some(h => h.fixable) && <button class="small-btn" disabled={repairing} onClick={runRepair}>{repairing ? 'Repairing…' : 'Repair'}</button>}
          <button class="small-btn" onClick={() => setDialog({ name: 'airepair' })}>Escalate to AI…</button>
        </div>
      )}
      {docId && findOpen && (() => {
        const st = view ? findKey.getState(view.state) : undefined;
        const requery = (patch: Partial<{ query: string; caseSensitive: boolean; wholeWord: boolean; regex: boolean; searchMath: boolean; selectionOnly: boolean }>, useSelection?: boolean) => {
          if (!view) return;
          setQuery(view, { query: findQ, caseSensitive: findCase, wholeWord: findWord, regex: findRegex, searchMath: findMath, selectionOnly: findSel, ...patch, ...(useSelection !== undefined ? { useSelection } : {}) });
        };
        return (
        <div class="find-bar-wrap">
        <div class="find-bar">
          <span>Find:</span><input ref={findInput} value={findQ} onInput={e => { const v = (e.target as HTMLInputElement).value; setFindQ(v); requery({ query: v }); }} onKeyDown={e => { if (e.key === 'Enter' && view) findNext(view, e.shiftKey ? -1 : 1); if (e.key === 'Escape') { setFindOpen(false); if (view) { setQuery(view, { query: '' }); view.focus(); } } }} />
          <label title="Case sensitive"><input type="checkbox" checked={findCase} onChange={e => { const v = (e.target as HTMLInputElement).checked; setFindCase(v); requery({ caseSensitive: v }); }} /> Aa</label>
          <label title="Whole words only"><input type="checkbox" checked={findWord} onChange={e => { const v = (e.target as HTMLInputElement).checked; setFindWord(v); requery({ wholeWord: v }); }} /> Word</label>
          <button class="small-btn" onClick={() => view && findNext(view, 1)}>Next</button>
          <button class="small-btn" onClick={() => view && findNext(view, -1)}>Prev</button>
          <span>Replace:</span><input value={replQ} onInput={e => setReplQ((e.target as HTMLInputElement).value)} />
          <button class="small-btn" onClick={() => view && replaceCurrent(view, replQ)}>Replace</button>
          <button class="small-btn" onClick={() => { if (view) notify(`Replaced ${replaceAll(view, replQ)} occurrence(s)`); }}>Replace all</button>
          <span style="color:#666">{st ? (st.error ? `regex error` : `${st.matches.length} matches`) : ''}</span>
          <button class={'small-btn' + (findAdv ? ' active' : '')} title="Advanced options" onClick={() => setFindAdv(a => !a)}>Advanced ▾</button>
          <span style="flex:1" /><button class="small-btn" onClick={() => { setFindOpen(false); if (view) { setQuery(view, { query: '' }); view.focus(); } }}>✕</button>
        </div>
        {findAdv && (
          <div class="find-bar find-bar-adv">
            <label title="Treat the search text as a regular expression ($1… back-references work in Replace)"><input type="checkbox" checked={findRegex} onChange={e => { const v = (e.target as HTMLInputElement).checked; setFindRegex(v); requery({ regex: v }); }} /> Regular expression</label>
            <label title="Also search inside math formulas"><input type="checkbox" checked={findMath} onChange={e => { const v = (e.target as HTMLInputElement).checked; setFindMath(v); requery({ searchMath: v }); }} /> Search math</label>
            <label title="Only search the current selection"><input type="checkbox" checked={findSel} onChange={e => { const v = (e.target as HTMLInputElement).checked; setFindSel(v); requery({ selectionOnly: v }, v); }} /> In selection</label>
            {st?.error && <span class="find-error">{st.error}</span>}
          </div>
        )}
        </div>
        );
      })()}
      <div class="main">
        {/* (hidden, its button is in the bottom-left corner: StatusBar `corner`) */}
        {showFiles && (
          <div class="sidebar left">
            <DocPanel current={textId} currentDoc={isLyxDoc ? docId : null} refreshKey={refreshKey} outline={outline} activePos={activePos} view={masterView}
              onOpen={(id, o) => openInTab(id, o)} onHide={() => setShowFiles(false)} onProject={setCurProject} notify={notify} />
          </div>
        )}
        {showFiles && <SidebarGrip side="left" />}
        {isLyxDoc && <ToolbarDock at="left">{toolbarRows}</ToolbarDock>}
        <div class={'editor-column panes' + (isLyxDoc && shownPanes.length > 1 ? ' split' : '')}>
        <div class={'editor-scroll' + (marginMode && isLyxDoc ? ' margin-mode' : '') + (inkMode && isLyxDoc ? ' ink-pan' : '')} ref={scrollRef} data-pane="doc" style={isLyxDoc ? paneStyle('doc') : undefined} onClick={e => { if (e.target === e.currentTarget && view) view.focus(); }}>
          {(isLyxDoc || isTextTab) && showRuler && <Ruler width={textWidth} onChange={setTextWidth} notes={isLyxDoc && marginMode ? { sash: notesPane.sash, scale: noteScale, onScale: setNoteScale, onFold: notesFold.fold } : undefined} />}
          {isLyxDoc && notesFold.folded && <NotesUnfoldTab onUnfold={notesFold.unfold} />}
          {docId ? (isPdfTab ? <div class="pdf-tab"><PdfViewer key={docId} url={fileUrl(projectOfDoc(textId!), docPathOf(textId!))} base={textId!} linkId={docId} place={place} toolbar={<a class="small-btn" href={fileUrl(projectOfDoc(textId!), docPathOf(textId!)) + '?download=1'}>Download</a>} /></div> : isBoardTab ? <BoardEditor key={docId} id={docId} user={user} notify={notify} /> : isHtmlTab ? <DeliverableEditor key={docId} id={docId} user={user} notify={notify} place={place} /> : htmlPending ? <div class="editor-loading" aria-busy="true" /> : isProjectRoot ? <ProjectRootPanel key={docId} project={docId} notify={notify} onCreated={id => { openInTab(id); setRefreshKey(k => k + 1); }} /> : !isLyxDoc ? <TextEditor key={docId} id={textId!} notify={notify} place={place} /> :
            <div class="editor-page">
              {marginMode && <NotesSash sash={notesPane.sash} onFold={notesFold.fold} />}
              {marginMode && <NotesResolved views={() => [masterView, ...[...childRefs.current.values()].map(h => h.view)].filter((v): v is EditorView => !!v)} tick={docTick} />}
              <div class="editor-host" ref={containerRef} />
              {combined && childIds.map(id => (
                <ChildEditor key={id + ':' + reloadKey} id={id} user={user} marginMode={marginMode} readOnly={viewOnly || viewing} onSelection={onSelection} onDocChange={() => { setDocTick(t => t + 1); }} onStale={resolveStale}
                  register={(cid, h) => { if (h) childRefs.current.set(cid, h); else childRefs.current.delete(cid); rerender(); }} />
              ))}
            </div>
          ) : <Home user={user} refreshKey={refreshKey} onOpen={id => openInTab(id)} onStartTour={id => { openInTab(id); openTour('steps'); }} onShare={p => setShareFor(p)} onGit={p => setGitFor(p)} onChanged={() => setRefreshKey(k => k + 1)} onBrowse={() => setShowFiles(true)} onSignIn={signIn} notify={notify} />}
        </div>
        {isLyxDoc && <SourcePane key={docId!} fill style={paneStyle('tex')} target={sourceTarget} tick={docTick} selTick={selTick} mathField={mathField} onNotify={notify} onClose={() => showPane('tex', false)} />}
        {isLyxDoc && pdfMounted && (
          <div class="pdf-pane" data-pane="pdf" style={paneStyle('pdf')}>
            {isArticle ? <ArticlePage key={docId} docId={docId!} savedAt={save.savedAt} view={view} selTick={selTick} syncSeq={pageSync} ruler={showRuler} onClose={() => showPane('pdf', false)} /> : <PdfPanel key={docId} docId={docId} state={pdf} savedAt={save.savedAt} onBuild={build} onCancel={cancelBuild} onShowTex={showTex} syncTarget={syncTarget} onForward={() => { void syncToPdf(); }} onInverse={(pg, x, y) => { void syncFromPdf(pg, x, y); }}
              onPublicLink={shareProject ? () => setShareFor(shareProject) : undefined} onClose={() => showPane('pdf', false)} />}
          </div>
        )}
        {isLyxDoc && !narrowPanes && <PaneDividers layout={panes} onChange={setPanesState} onResizeEnd={saveLayout} />}
        </div>
        {isLyxDoc && <ToolbarDock at="right">{toolbarRows}</ToolbarDock>}
        {isLyxDoc && !rightTab && (
          <div class="rail right">
            {RIGHT_TABS.filter(t => t !== 'agent' || agentTab).map(t => <button key={t} data-rail={t} title={RIGHT_TAB_TITLES[t]} onClick={() => { setRightTab(t); if (t === 'versions') setSelVersion(v => v + 1); }}>{RIGHT_TAB_LABELS[t]}</button>)}
            <button data-rail="pdf" class={panes.shown.pdf ? 'active' : ''} title={PDF_TITLE} onClick={() => flipPane('pdf')}>PDF</button>
            <button data-rail="source" class={panes.shown.tex ? 'active' : ''} title={SOURCE_TITLE} onClick={toggleRawSplit}>Source</button>
          </div>
        )}
        {isLyxDoc && rightTab && <SidebarGrip side="right" />}
        {isLyxDoc && rightTab && (
          <div class="sidebar right">
            <div class="panel-tabs">
              <button class={rightTab === 'comments' ? 'active' : ''} data-tab="comments" onClick={() => setRightTab('comments')} title={RIGHT_TAB_TITLES.comments}>Comments</button>
              <button class={rightTab === 'versions' ? 'active' : ''} data-tab="versions" onClick={() => { setRightTab('versions'); setSelVersion(v => v + 1); }} title={RIGHT_TAB_TITLES.versions}>Versions</button>
              {agentTab && <button class={rightTab === 'agent' ? 'active' : ''} data-tab="agent" onClick={() => setRightTab('agent')} title={RIGHT_TAB_TITLES.agent}>Agent</button>}
              <button class={'toggle' + (panes.shown.pdf ? ' on' : '')} data-tab="pdf" onClick={() => flipPane('pdf')} title={PDF_TITLE}>PDF</button>
              <button class={'toggle' + (panes.shown.tex ? ' on' : '')} data-tab="source" onClick={toggleRawSplit} title={SOURCE_TITLE}>Source</button>
              <button class="hide" title="Hide the sidebar" onClick={() => setRightTab(null)}>»</button>
            </div>
            {rightTab === 'comments' && <div class="panel-body"><Comments views={[masterView, ...[...childRefs.current.values()].map(h => h.view)].filter((v): v is EditorView => !!v)} tick={docTick} /></div>}
            {rightTab === 'versions' && <div class="panel-body"><Versions docId={docId} refreshKey={selVersion} /></div>}
            {rightTab === 'agent' && <AgentPanel project={projectOfDoc(docId)} notify={notify} codex={aiActivated} />}
          </div>
        )}
      </div>
      {/* Contextual toolbars, docked above the status bar like LyX. They are an overlay
          (.bottom-toolbars is absolutely positioned), so their coming and going with the cursor
          never shifts the document. */}
      {/* (each can be dragged by its name to another edge of the document: app/ctxdock.tsx) */}
      {isLyxDoc && <ContextBars style={{ left: showFiles ? 'var(--left-width, 272px)' : '0px', right: rightTab ? 'var(--right-width, 360px)' : '24px' }} bars={contextBars([
        [tb.showMath, 'math', <Toolbar id="math" label="Math" groups={tb.math} />],
        [tb.showMath && tbMode('mathpanels') !== 'off', 'mathpanels', <Toolbar id="mathpanels" label="Panels" groups={tb.mathPanels} />],
        [tb.showTable, 'table', <Toolbar id="table" label="Table" groups={tb.table} />],
        [tb.showReview, 'review', <Toolbar id="review" label="Review" groups={tb.review} />],
        [inkMode && !getInk().folded, 'ink', <Toolbar id="ink" label="Draw" groups={inkGroups} />],
      ])} />}
      {/* the quick menu over selected words (toolbars.tsx `quick`) */}
      {isLyxDoc && <SelectionBar view={view} groups={tb.quick} />}
      <StatusBar layout={layout} markdown={isMarkdownDoc(docId) || isArticle} status={status} chord={chord} message={message} save={save} tracking={tracking} trackingAs={user.name} change={changeInfo}
        docLabel={view && masterView && view !== masterView ? viewDocId(view).split('/').pop() ?? null : null}
        readOnly={!!docId && viewOnly} updateReady={updateReady} aiBusy={aiBusy > 0}
        quiet={!!docId && !isLyxDoc} stats={docStats} pageInfo={pagesOn && masterView && isLyxDoc ? pageAtCursor(masterView.state) : null} zoom={zoom} onZoom={setZoom}
        corner={!showFiles && <CornerButton icon="files" title={LEFT_TITLE} data-rail="outline" onClick={() => setShowFiles(true)}>Files</CornerButton>}
        sync={syncChip}
        pdf={isLyxDoc ? { state: pdf, onClick: rebuild => { if (rebuild && !pdf.busy) void build(); else if (!pdfPaneVisible) changePanes(l => (narrowPanes ? { ...setPaneShown(l, 'pdf', true), order: ['pdf', ...l.order.filter(p => p !== 'pdf')] } : setPaneShown(l, 'pdf', true))); } } : undefined} />
      {renderDialog()}
      {linkAsk && <CopyLinkDialog url={linkAsk.url} what={linkAsk.what} user={user} notify={notify} onClose={() => setLinkAsk(null)} />}
      {shareFor && <ShareDialog project={shareFor} user={user} doc={docId?.replace(/^(text|pdf):/, '') ?? null} view={isLyxDoc ? viewParam(panes, pagesOn, viewKindOf(docId!)) : null} onClose={() => setShareFor(null)} onChanged={() => setRefreshKey(k => k + 1)} />}
      {newLayoutFor && <NewLayoutDialog project={newLayoutFor} dir={textId ? docPathOf(textId).split('/').slice(0, -1).join('/') : ''} notify={notify} onClose={() => setNewLayoutFor(null)} onCreated={id => { location.hash = '#/' + id; setRefreshKey(k => k + 1); }} />}
      {gitFor && <GitDialog project={gitFor} user={user} onClose={() => setGitFor(null)} />}
      {askInterface && !tour && <InterfacePrompt onDone={() => setAskInterface(false)} />}
      {tour && <Tour key={tourRequest} intro={tour === 'intro'} onEnd={endTour}
        ctx={{ docId, ready: isLyxDoc && status.synced && !!view, docTick, layout, inMath: !!mathField, saveState: save.state, rightTab, pdfBusy: pdf.busy, pdfBuiltAt: pdf.builtAt ?? 0, shareOpen: !!shareFor, gitOpen: !!gitFor, marginMode }}
        actions={{ openExample, showRight: () => { if (!rightTab) setRightTab('comments'); }, showPdf: () => showPane('pdf'), showFiles: () => setShowFiles(true) }} />}
      {/* last: any uiPrompt/uiConfirm/uiAlert (Dialogs.tsx) stacks above every other dialog above, including Home's and Git's */}
      <SvgEditorHost />
      <DialogHost />
    </div>
  );
}

/** Child documents (\include / \input of .tex files) of a master, in document order. */
function collectChildren(view: EditorView): string[] {
  const out: string[] = [];
  const project = view.dom.dataset.project ?? '', docDir = view.dom.dataset.docDir ?? '';
  view.state.doc.descendants((node) => {
    let id = C.includeTarget(node, project, docDir);
    if (id && !/\.[A-Za-z0-9]+$/.test(id)) id += '.tex';
    if (id && id.endsWith('.tex') && !out.includes(id)) out.push(id);
    return true;
  });
  return out;
}

function ChildEditor({ id, user, marginMode, readOnly, onSelection, onDocChange, onStale, register }: { id: string; user: User; marginMode: boolean; readOnly?: boolean; onSelection: (v: EditorView) => void; onDocChange: () => void; onStale: (handle: EditorHandle, id: string, pending: boolean) => Promise<void>; register: (id: string, h: EditorHandle | null) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<Status>({ connected: false, synced: false, users: [] });
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let handle: EditorHandle | null = null;
    let cancelled = false;
    // the child's metadata (macros inherited from the master) must exist before we connect
    api.meta(id).then(m => {
      if (cancelled || !ref.current) return;
      ref.current.innerHTML = '';
      handle = createEditor({ docId: id, user, container: ref.current, marginMode, child: true, onStatus: setStatus, onSelectionChange: onSelection, onDocChange,
        onStale: info => { if (handle) void onStale(handle, id, info.pendingLocal); } });
      register(id, handle);
      if (readOnly || m.role === 'view') { handle.setViewOnly(true); handle.setEditable(false); }
      refreshMacros(handle.view, m.macros, true);
      handle.provider.on('sync', () => { if (handle) refreshMacros(handle.view, m.macros, true); });
    }).catch(e => { if (!cancelled) setError(String((e as Error).message)); });
    return () => { cancelled = true; if (handle) { register(id, null); handle.destroy(); } };
  }, [id]);
  return (
    <div class="child-doc">
      <div class="child-doc-header">
        <span class="name">📄 {id.split('/').pop()}</span>
        <span class="path">{id}</span>
        <span style="flex:1" />
        <span class="sync">{error ? 'not available' : status.connected ? (status.synced ? 'connected' : 'syncing…') : 'connecting…'}</span>
        {!error && <a href={'#/' + id} class="small-btn" title="Open this child document in its own tab">Open in tab</a>}
      </div>
      {error ? <div class="child-doc-error">Child document cannot be opened: {error}</div> : <div class="editor-host child" ref={ref} />}
    </div>
  );
}

export { schema };
